import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform, type Variants } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  Check,
  Menu,
  X,
  Phone,
  Mail,
  Award,
  CheckCircle2,
  ShieldCheck,
  UserCheck,
  Stethoscope,
  ClipboardCheck,
  PenSquare,
  Plane,
  Sparkles,
  Activity,
  CalendarClock,
  Star,
  GraduationCap,
  BookOpen,
  Globe2,
  Users,
  ArrowRight,
  Ruler,
  Zap,
  Maximize2,
  Compass,
  HelpCircle,
  Trophy,
  Hotel,
  Car,
  Scissors,
  Building2,
  MapPin,
  Lock,
  Microscope,
  TrendingUp,
  Play,
  type LucideIcon,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { FaWhatsapp } from "react-icons/fa";
import { SiTrustpilot } from "react-icons/si";
import { FcGoogle } from "react-icons/fc";
import { MdVerified } from "react-icons/md";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PhoneField } from "@/components/PhoneField";

import lightLogo from "@/assets/rememore_light.png";
import drAlpha from "@/assets/dr_alpha_new.png";
import jciCertificate from "@/assets/jci_certificate.png";
import pressMemorial from "@/assets/1_1777880054420.png";
import pressWashingtonTimes from "@/assets/2_1777880054420.png";
import pressYahooFinance from "@/assets/3_1777880054420.png";
import pressIBT from "@/assets/4_1777880054420.png";
import pressLifeStyle from "@/assets/5_1777880054421.png";
import pressNYWeekly from "@/assets/6_1777880054421.png";
import pressDailyCaller from "@/assets/7_1777880054421.png";
import pressTechBullion from "@/assets/8_1777880054421.png";
import pressFirst from "@/assets/9_1777880054421.png";
import pressBenzinga from "@/assets/10_1777880054421.png";
import pressMarketsInsider from "@/assets/11_1777880054422.png";
import pressFox8 from "@/assets/12_1777880054422.png";
import pressWikipedia from "@/assets/13_1777880054422.png";
import img1 from "@/assets/img1_1777370780496.png";
import img2 from "@/assets/img2_1777370780495.png";
import img3 from "@/assets/img3_1777370780495.png";
import img4 from "@/assets/img4_1777370780494.png";
import hospBahcelievler from "@/assets/bahcelievler_1777383982614.webp";
import hospSisli from "@/assets/sisli-web_555x376px-1jpg_e49e_kopyası_1777385221519.png";
import hospBodrum from "@/assets/bodrum-hastanesijpg_b79a_1777383982614.webp";
import hospGoztepe from "@/assets/goztepe-web_555x376pxjpg_db7d_1777383982614.webp";
import hospAtasehir from "@/assets/atasehir-web_555x376pxjpg_6731_1777385172890.webp";
import hospAnkara from "@/assets/ankara-web_555x376px-1jpg_9c70_1777383982613.webp";
import sixpackBg from "@/assets/sixpack-bg.png";
import luxuryVipBg from "@/assets/luxury-vip-bg.png";
import whyusVipBg from "@/assets/whyus-vip-bg.png";
import patientMR from "@/assets/patient_mr.png";
import patientDK from "@/assets/patient_dk.png";
import patientPA from "@/assets/patient_pa.png";
import patientBL from "@/assets/patient_bl.png";
import patientGV from "@/assets/patient_gv.png";
import patientCM from "@/assets/patient_cm.png";
import patientFS from "@/assets/patient_fs.png";
import patientEJ from "@/assets/patient_ej.png";
import patientVP from "@/assets/patient_vp.png";
import patientOT from "@/assets/patient_ot.png";
import whyusLuxuryBg from "@/assets/whyus-luxury-bg.png";
import journeyProtocolBg from "@/assets/journey-protocol-bg.png";
import journeyBg from "@/assets/whymen_1777375297047.png";

void sixpackBg;
void luxuryVipBg;
void whyusVipBg;
void journeyProtocolBg;
void journeyBg;

const WHATSAPP_LINK =
  "https://api.whatsapp.com/send/?phone=905491785724&text&type=phone_number&app_absent=0";
const GOOGLE_LINK = "https://share.google/2PhqUDVyBxUJENHzT";
const TRUSTPILOT_LINK = "https://www.trustpilot.com/review/rememore.com";
const PHONE_LINK = "tel:+908502143436";
const EMAIL_LINK = "mailto:info@rememore.com";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const NAV_LINKS: { id: string; label: string; Icon: LucideIcon }[] = [
  { id: "why-us", label: "Why Us", Icon: ShieldCheck },
  { id: "procedures", label: "Procedures", Icon: Stethoscope },
  { id: "plan", label: "Plan", Icon: Sparkles },
  { id: "istanbul", label: "Destination", Icon: MapPin },
  { id: "hospitals", label: "Hospitals", Icon: Building2 },
  { id: "faq", label: "FAQ", Icon: HelpCircle },
];

const formSchema = z
  .object({
    fullName: z.string().min(2, "Full name is required"),
    email: z.string().email("Valid email is required"),
    phone: z.string().min(1, "Phone number is required"),
    phonePrefix: z.string(),
    phoneCountry: z.string(),
    phoneValid: z.boolean(),
    consent: z.boolean().refine((val) => val, "Consent is required"),
    recaptchaToken: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.phone && !data.phoneValid) {
      ctx.addIssue({
        path: ["phone"],
        code: z.ZodIssueCode.custom,
        message: "Enter a valid phone number for the selected country",
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

export default function Home() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const heroVideoRef = useRef<HTMLIFrameElement>(null);
  const istanbulVideoRef = useRef<HTMLVideoElement>(null);
  const faqColRef = useRef<HTMLDivElement>(null);
  const [faqHeight, setFaqHeight] = useState<number | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  // Hospital marquee — JS-driven so the user can drag with finger / cursor.
  const hospOuterRef = useRef<HTMLDivElement>(null);
  const hospInnerRef = useRef<HTMLDivElement>(null);

  // Press marquee — JS-driven so the user can drag the logos.
  const pressOuterRef = useRef<HTMLDivElement>(null);
  const pressInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const outer = pressOuterRef.current;
    const inner = pressInnerRef.current;
    if (!outer || !inner) return;

    let offset = 0;
    let half = 0;
    let last = performance.now();
    let dragging = false;
    let startX = 0;
    let startOffset = 0;
    let pointerId: number | null = null;
    const speedPxPerSec = 35;

    const measure = () => {
      half = inner.scrollWidth / 2;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(inner);

    let raf = 0;
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (!dragging && half > 0) {
        offset -= speedPxPerSec * dt;
      }
      if (half > 0) {
        while (offset <= -half) offset += half;
        while (offset > 0) offset -= half;
      }
      inner.style.transform = `translate3d(${offset}px,0,0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onDown = (e: PointerEvent) => {
      dragging = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startOffset = offset;
      try { outer.setPointerCapture(e.pointerId); } catch { }
      outer.style.cursor = "grabbing";
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging || pointerId !== e.pointerId) return;
      offset = startOffset + (e.clientX - startX);
      e.preventDefault();
    };
    const onUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      pointerId = null;
      try {
        if (outer.hasPointerCapture(e.pointerId)) outer.releasePointerCapture(e.pointerId);
      } catch { }
      outer.style.cursor = "grab";
    };

    outer.style.cursor = "grab";
    outer.addEventListener("pointerdown", onDown);
    outer.addEventListener("pointermove", onMove);
    outer.addEventListener("pointerup", onUp);
    outer.addEventListener("pointercancel", onUp);
    outer.addEventListener("pointerleave", onUp);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      outer.removeEventListener("pointerdown", onDown);
      outer.removeEventListener("pointermove", onMove);
      outer.removeEventListener("pointerup", onUp);
      outer.removeEventListener("pointercancel", onUp);
      outer.removeEventListener("pointerleave", onUp);
    };
  }, []);

  useEffect(() => {
    const outer = hospOuterRef.current;
    const inner = hospInnerRef.current;
    if (!outer || !inner) return;

    let offset = 0;
    let half = 0;
    let last = performance.now();
    let dragging = false;
    let startX = 0;
    let startOffset = 0;
    let pointerId: number | null = null;
    const speedPxPerSec = 45;

    const measure = () => {
      half = inner.scrollWidth / 2;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(inner);

    let raf = 0;
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (!dragging && half > 0) {
        offset -= speedPxPerSec * dt;
      }
      if (half > 0) {
        while (offset <= -half) offset += half;
        while (offset > 0) offset -= half;
      }
      inner.style.transform = `translate3d(${offset}px,0,0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onDown = (e: PointerEvent) => {
      dragging = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startOffset = offset;
      try {
        outer.setPointerCapture(e.pointerId);
      } catch { }
      outer.style.cursor = "grabbing";
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging || pointerId !== e.pointerId) return;
      offset = startOffset + (e.clientX - startX);
      e.preventDefault();
    };
    const onUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      pointerId = null;
      try {
        if (outer.hasPointerCapture(e.pointerId)) {
          outer.releasePointerCapture(e.pointerId);
        }
      } catch { }
      outer.style.cursor = "grab";
    };

    outer.style.cursor = "grab";
    outer.addEventListener("pointerdown", onDown);
    outer.addEventListener("pointermove", onMove);
    outer.addEventListener("pointerup", onUp);
    outer.addEventListener("pointercancel", onUp);
    outer.addEventListener("pointerleave", onUp);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      outer.removeEventListener("pointerdown", onDown);
      outer.removeEventListener("pointermove", onMove);
      outer.removeEventListener("pointerup", onUp);
      outer.removeEventListener("pointercancel", onUp);
      outer.removeEventListener("pointerleave", onUp);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track desktop breakpoint (lg = 1024px). Stories col only matches FAQ height on desktop.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  async function sha256(value: string): Promise<string> {
    const normalizedValue = value.trim().toLowerCase();

    const encoder = new TextEncoder();
    const data = encoder.encode(normalizedValue);

    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  // Sync stories column height to FAQ column height (desktop only)
  useEffect(() => {
    const el = faqColRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setFaqHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Force-play both background videos via YouTube IFrame API postMessage.
  // Fires on load, on every YouTube event (onReady/onStateChange), and on a
  // timed fallback so the pause overlay is cleared regardless of autoplay policy.
  useEffect(() => {
    const playCmd = JSON.stringify({ event: "command", func: "playVideo", args: [] });

    const sendPlay = () => {
      heroVideoRef.current?.contentWindow?.postMessage(playCmd, "*");
      // Istanbul section uses an HTML5 <video> tag; force muted (React quirk
      // where the JSX prop sometimes does not stick) and nudge it to play.
      const v = istanbulVideoRef.current;
      if (v) {
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        v.play().catch(() => { });
      }
    };

    // Respond to any message from the YouTube players (onReady, onStateChange, etc.)
    const handleMessage = (e: MessageEvent) => {
      if (!e.data) return;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (d.event === "onReady" || d.event === "onStateChange" || d.info !== undefined) {
          sendPlay();
        }
      } catch (_) { }
    };

    window.addEventListener("message", handleMessage);

    // Fire at 200 ms, 600 ms, 1.2 s, 2 s, 3 s after mount to catch slow-loading players
    const timers = [200, 600, 1200, 2000, 3000].map((ms) => setTimeout(sendPlay, ms));

    // Re-fire on every iframe load event (e.g., loop restart)
    const sendOnLoad = () => sendPlay();
    heroVideoRef.current?.addEventListener("load", sendOnLoad);
    istanbulVideoRef.current?.addEventListener("load", sendOnLoad);

    return () => {
      window.removeEventListener("message", handleMessage);
      timers.forEach(clearTimeout);
      heroVideoRef.current?.removeEventListener("load", sendOnLoad);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      istanbulVideoRef.current?.removeEventListener("load", sendOnLoad);
    };
  }, []);

  useEffect(() => {
    const scrollFromHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash) return;
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 20) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      setTimeout(() => tryScroll(), 200);
    };
    scrollFromHash();
    window.addEventListener("hashchange", scrollFromHash);
    return () => window.removeEventListener("hashchange", scrollFromHash);
  }, []);

  const form = useForm<FormValues>({
  resolver: zodResolver(formSchema),
  defaultValues: {
    fullName: "",
    email: "",
    phone: "",
    phonePrefix: "+44",
    phoneCountry: "United Kingdom",
    phoneValid: false,
    consent: false,
    recaptchaToken: "",
  },
});

  const getUrlParameter = function getUrlParameter(sParam:any) {
    let sPageURL = window.location.search.substring(1),
      sURLVariables = sPageURL.split('&'),
      sParameterName,
      i;

    for (i = 0; i < sURLVariables.length; i++) {
      sParameterName = sURLVariables[i].split('=');

      if (sParameterName[0] === sParam) {
        return sParameterName[1] === undefined ? true : decodeURIComponent(sParameterName[1]);
      }
    }
    return "";
  };

  function get_ga_clientid(): string {
    const gaCookie = document.cookie
      .split(";")
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith("_ga="));

    return gaCookie ? gaCookie.split("=")[1] : "";
  }

    async function onSubmit(
        values: z.infer<typeof formSchema> & { recaptchaToken?: string }
      ) {
        try {
          const nameParts = values.fullName.trim().split(" ");
          const name = nameParts[0];
          const surname = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

          // CRM expects the national number only (Prefix is sent separately).
          // values.phone is the full E.164 number (e.g. "+447911123456"), so
          // strip the leading dial code; fall back to dropping a leading "+".
          const phonePrefix = values.phonePrefix;
          const phoneNumber =
            phonePrefix && values.phone.startsWith(phonePrefix)
              ? values.phone.slice(phonePrefix.length)
              : values.phone.replace(/^\+/, "");

          const formData = new FormData();

          formData.append("Name", name);
          formData.append("Surname", surname);
          formData.append("Email", values.email);
          formData.append("Phone", phoneNumber);
          formData.append("Prefix", phonePrefix);
          formData.append("Description", "Operation: Male Aesthetics");
          formData.append("Language", "100000011");
          formData.append("CaseOriginCode", "100000007");
          formData.append("DoctorId", "");
          formData.append("HospitalId", "");
          formData.append("SectionId", "");
          formData.append("SubOriginCode", "100000000");
          formData.append("Subject", "100000003");
          formData.append("SourceUrl", typeof window !== "undefined" ? window.location.href : "");
          formData.append("Campaign", "");
          formData.append("g-recaptcha-response", values.recaptchaToken || "");
          formData.append("Utm_Source", String(getUrlParameter("utm_source")));
          formData.append("Utm_Campaign", String(getUrlParameter("utm_campaign")));
          formData.append("Utm_Content", String(getUrlParameter("utm_content")));
          formData.append("Utm_Medium", String(getUrlParameter("utm_medium")));
          formData.append("Utm_Term", String(getUrlParameter("utm_term")));
          formData.append("FacebookAdSet", "");
          formData.append("LeadChannel", "100000004");
          formData.append("LeadSource", "100000000");
          formData.append("isKvkkApproved", values.consent ? "true" : "false");
          formData.append("isETKApproved", "true");
          formData.append("FacebookUsername", "1");
          formData.append("InstagramUsername", "1");
          formData.append("FormSourceCode", "100000005");
          formData.append("RequestId", "42853ead-014f-4197-b732-18976df7507c");
          formData.append("Ga_UserId", "af67be2b8ad921d859b160ee676653f3bec9a41c8a0de1e8a20ea3d60cc260e0");
          formData.append("Ga_ClientId", get_ga_clientid());
          formData.append("Country", values.phoneCountry);
          formData.append("Type", "undefined");
          formData.append("Documents", "");
          formData.append("lang2IsoCode", "en");
          formData.append("CookieGoogle", "undefined");
          formData.append("CookieFacebook1", "undefined");
          formData.append("CookieFacebook2", "undefined");
          formData.append("TiktokCookie1", "undefined");
          formData.append("TiktokCookie2", "undefined");
          

          const response = await fetch("/form", {
            method: "POST",
            headers: {
              "X-Referer": typeof window !== "undefined" ? window.location.href : "",
            },
            body: formData,
          });

          if (response.ok) {
            const hashedEmail = await sha256(values.email);

            window.dataLayer = window.dataLayer || [];

            window.dataLayer.push({
              event: "form_submission",
              form_id: "dr_alpha",
              email: hashedEmail,
              form_status: "success",
            });

            setFormSuccess(true); 
            form.reset();
          } else {
            console.error("CRM send error:", response.statusText);
            alert("An error occurred, please try again later.");
          }
        } catch (error) {
          console.error("An error occurred during submission:", error);
          alert("A connection error occurred.");
        }
      }

      const fadeIn: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.8, ease: "easeOut" },
        },
      };

      const staggerContainer: Variants = {
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: { staggerChildren: 0.15 },
        },
      };

      const scrollTo = (id: string) => {
        setIsNavOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };

      const openWhatsApp = () => window.open(WHATSAPP_LINK, "_blank");

      const openConsultation = () => {
        setIsNavOpen(false);
        setFormSuccess(false);
        setFormOpen(true);
      };

      return (
        <GoogleReCaptchaProvider reCaptchaKey="6LcRuqgpAAAAAKCcgyREbGkcKQbdGQQ4mtn2M1f5" scriptProps={{ async: true, defer: true, appendTo: "head", nonce: undefined, }} >
          <div className="min-h-[100dvh] bg-background flex flex-col font-sans">
            {/* Top Nav */}
            <header
              className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled
                  ? "bg-background/85 backdrop-blur-xl border-b border-border/40 py-3"
                  : "bg-transparent py-5"
                }`}
            >
              <div className="container mx-auto px-6 flex items-center justify-between gap-6">
                <a
                  href="#"
                  className="inline-block flex-shrink-0"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo("hero");
                  }}
                >
                  <img
                    src={lightLogo}
                    alt="rememore"
                    className="h-7 md:h-8 object-contain translate-y-[2px] md:translate-y-[3px]"
                  />
                </a>

                <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold tracking-wide text-foreground/85">
                  {NAV_LINKS.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => scrollTo(link.id)}
                      className="hover:text-primary transition-colors"
                    >
                      {link.label}
                    </button>
                  ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                  <Button
                    variant="outline"
                    className="border-white/15 hover:bg-white/5 hover:text-white rounded-full h-11 px-5 font-semibold text-sm bg-white/[0.02]"
                    onClick={openWhatsApp}
                  >
                    <FaWhatsapp className="mr-2 w-4 h-4 text-green-400" />
                    WhatsApp
                  </Button>
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 h-11 font-bold tracking-wide text-sm shadow-[0_0_20px_rgba(200,165,110,0.25)]"
                    onClick={openConsultation}
                  >
                    Book Consultation
                  </Button>
                </div>

                <button
                  className="lg:hidden text-foreground"
                  onClick={() => setIsNavOpen(!isNavOpen)}
                  aria-label="Toggle menu"
                >
                  {isNavOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
              </div>
            </header>

            {/* Mobile Nav Overlay */}
            <AnimatePresence>
              {isNavOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40 bg-gradient-to-b from-background/97 via-background/95 to-background/97 backdrop-blur-2xl pt-24 pb-10 px-5 lg:hidden overflow-y-auto"
                >
                  {/* Decorative ambient glow */}
                  <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-primary/15 blur-[100px]"></div>

                  <div className="relative max-w-md mx-auto">
                    {/* Header tag */}
                    <div className="flex items-center justify-center mb-6">
                      <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-3.5 py-1.5 bg-white/[0.04] backdrop-blur-md">
                        <Lock className="w-3 h-3 text-primary" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-foreground/70 font-extrabold">
                          Discreet & Private
                        </span>
                      </div>
                    </div>

                    <nav className="flex flex-col gap-2">
                      {NAV_LINKS.map((link, i) => (
                        <motion.button
                          key={link.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 + i * 0.05, duration: 0.35 }}
                          onClick={() => scrollTo(link.id)}
                          className="group flex items-center gap-4 w-full text-left bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-2xl px-4 py-3.5 transition-all"
                        >
                          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                            <link.Icon className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-base font-extrabold tracking-wide text-foreground/90 group-hover:text-white transition-colors flex-1">
                            {link.label}
                          </span>
                          <ArrowRight className="w-4 h-4 text-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                        </motion.button>
                      ))}
                    </nav>

                    {/* CTAs */}
                    <div className="mt-7 flex flex-col gap-3">
                      <Button
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-14 text-sm w-full font-extrabold tracking-[0.15em] uppercase shadow-[0_0_30px_rgba(200,165,110,0.4)]"
                        onClick={openConsultation}
                      >
                        Book Consultation
                      </Button>
                      <Button
                        variant="outline"
                        className="border-white/15 hover:bg-white/5 rounded-full h-14 text-sm w-full font-extrabold tracking-[0.15em] uppercase bg-white/[0.03]"
                        onClick={openWhatsApp}
                      >
                        <FaWhatsapp className="mr-2 w-5 h-5 text-green-400" />
                        WhatsApp Now
                      </Button>
                    </div>

                    {/* Trust mini-row */}
                    <div className="mt-8 flex items-center justify-center gap-4 opacity-70">
                      <div className="flex items-center gap-1.5">
                        <SiTrustpilot className="w-3 h-3 text-green-400" />
                        <span className="text-[10px] uppercase tracking-wider font-bold">4.8</span>
                      </div>
                      <div className="w-px h-3 bg-white/15"></div>
                      <div className="flex items-center gap-1.5">
                        <FcGoogle className="w-3 h-3" />
                        <span className="text-[10px] uppercase tracking-wider font-bold">4.9</span>
                      </div>
                      <div className="w-px h-3 bg-white/15"></div>
                      <div className="flex items-center gap-1.5">
                        <Award className="w-3 h-3 text-primary" />
                        <span className="text-[10px] uppercase tracking-wider font-bold">JCI</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <main className="flex-grow">
              {/* HERO */}
              <section
                id="hero"
                className="relative min-h-[92dvh] flex items-center pt-24 pb-16 overflow-hidden"
              >
                <div className="absolute inset-x-0 top-0 h-[70dvh] md:inset-0 md:h-auto z-0 bg-background pointer-events-none overflow-hidden">
                  <iframe
                    ref={heroVideoRef}
                    src="https://player.vimeo.com/video/1076034759?h=c284ad24ee&background=1&autoplay=1&loop=1&muted=1&autopause=0&playsinline=1&controls=0&title=0&byline=0&portrait=0&dnt=1"
                    className="absolute opacity-100 pointer-events-none hero-video-iframe"
                    style={{
                      top: "50%",
                      left: "50%",
                      clipPath: "inset(0 0 9% 0)",
                    }}
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    title="Hero background video"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  {/* Edge scrims — softened so the video reads brightly */}
                  <div className="absolute inset-0 z-10 bg-gradient-to-r from-background/75 via-background/15 to-transparent pointer-events-none"></div>
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-background via-background/30 to-transparent pointer-events-none"></div>
                  {/* Bottom-only fade so hero blends smoothly into the next section without darkening the middle */}
                  <div
                    className="absolute inset-x-0 bottom-0 h-[45%] z-10 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(to top, hsl(var(--background)) 0%, hsl(var(--background)/0.7) 35%, hsl(var(--background)/0.25) 70%, transparent 100%)",
                    }}
                  ></div>
                  {/* Brief fading center scrim — hides the YouTube initial play button while autoplay kicks in, then disappears */}
                  <div className="absolute inset-0 z-20 pointer-events-none yt-play-suppress"></div>
                </div>

                <div className="container relative z-20 px-6 mx-auto grid lg:grid-cols-2 gap-14 items-center">
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={staggerContainer}
                    className="max-w-2xl"
                  >
                    <motion.div
                      variants={fadeIn}
                      className="flex items-center gap-3 mb-6"
                    >
                      <div className="w-6 h-[2px] bg-primary rounded-full"></div>
                      <span className="gold-shimmer-bright text-xs font-bold tracking-[0.2em] uppercase">
                        Swiss-Quality Craftsmanship
                      </span>
                    </motion.div>

                    <motion.h1
                      variants={fadeIn}
                      className="font-sans font-extrabold leading-[1.02] mb-5 md:mb-8 text-foreground flex flex-col tracking-tight"
                    >
                      <span className="text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem]">
                        MASTERY IN
                      </span>
                      <span className="text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem] text-primary">
                        MALE AESTHETICS
                      </span>
                    </motion.h1>

                    <motion.p
                      variants={fadeIn}
                      className="text-[11px] sm:text-base md:text-xl text-foreground/85 mb-6 md:mb-10 leading-relaxed font-medium max-w-xl whitespace-nowrap md:whitespace-normal"
                    >
                      Discreet, world-class surgical enhancements for the<br />
                      modern man. Precision techniques, uncompromising<br />
                      safety, and lifetime confidence
                    </motion.p>

                    {/* MOBILE-ONLY consultation form (same style as desktop, in hero) */}
                    <motion.div
                      variants={fadeIn}
                      className="lg:hidden w-full mb-6"
                    >
                      <div className="relative">
                        <div className="absolute -inset-4 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[2.5rem] blur-3xl pointer-events-none"></div>
                        <div className="shine-border relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[1.75rem] p-5 sm:p-6 shadow-[0_25px_60px_rgba(0,0,0,0.5)]">
                          <ConsultationCard
                            form={form}
                            onSubmit={onSubmit}
                            formSuccess={formSuccess}
                            resetForm={() => {
                              form.reset();
                              setFormSuccess(false);
                            }}
                            onPrivacyClick={() => setPrivacyOpen(true)}
                          />
                        </div>
                      </div>
                    </motion.div>

                    {/* Desktop/tablet CTA buttons (mobile uses the form above + sticky bar) */}
                    <motion.div
                      variants={fadeIn}
                      className="hidden sm:flex flex-col sm:flex-row gap-4 mb-10 w-full"
                    >
                      <Button
                        className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 rounded-full font-bold tracking-wide uppercase text-sm shadow-[0_0_25px_rgba(200,165,110,0.35)]"
                        onClick={openConsultation}
                      >
                        Book Private Consultation
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-white/20 text-foreground hover:bg-white/5 h-14 px-8 rounded-full font-bold tracking-wide uppercase text-sm bg-white/[0.03] backdrop-blur-sm"
                        onClick={openWhatsApp}
                      >
                        <FaWhatsapp className="mr-2 w-5 h-5 text-green-400" />{" "}
                        WhatsApp Now
                      </Button>
                    </motion.div>

                    {/* MOBILE — separated trust tags (3 pills, single row) */}
                    <motion.div
                      variants={fadeIn}
                      className="sm:hidden flex flex-nowrap items-stretch gap-1.5 w-full"
                    >
                      <a
                        href={TRUSTPILOT_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 flex items-center gap-1.5 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-2 py-1.5 hover:bg-white/[0.09] transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]"
                      >
                        <div className="bg-green-500/20 p-0.5 rounded-full flex-shrink-0">
                          <SiTrustpilot className="w-2.5 h-2.5 text-green-400" />
                        </div>
                        <div className="flex flex-col leading-tight min-w-0">
                          <span className="text-[7px] uppercase tracking-wider text-foreground/60 font-bold">
                            Trustpilot
                          </span>
                          <span className="text-[9px] font-bold whitespace-nowrap">4.8 / 5</span>
                        </div>
                      </a>
                      <a
                        href={GOOGLE_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 flex items-center gap-1.5 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-2 py-1.5 hover:bg-white/[0.09] transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]"
                      >
                        <div className="bg-white/10 p-0.5 rounded-full flex-shrink-0">
                          <FcGoogle className="w-2.5 h-2.5" />
                        </div>
                        <div className="flex flex-col leading-tight min-w-0">
                          <span className="text-[7px] uppercase tracking-wider text-foreground/60 font-bold">
                            Google
                          </span>
                          <span className="text-[9px] font-bold whitespace-nowrap">4.9 · 220+</span>
                        </div>
                      </a>
                      <div className="flex-1 min-w-0 flex items-center gap-1.5 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]">
                        <div className="bg-primary/20 p-0.5 rounded-full flex-shrink-0">
                          <Award className="w-2.5 h-2.5 text-primary" />
                        </div>
                        <div className="flex flex-col leading-tight min-w-0">
                          <span className="text-[7px] uppercase tracking-wider text-foreground/60 font-bold">
                            JCI
                          </span>
                          <span className="text-[9px] font-bold whitespace-nowrap">Certified</span>
                        </div>
                      </div>
                    </motion.div>

                    {/* DESKTOP/TABLET — single pill trust strip */}
                    <motion.div
                      variants={fadeIn}
                      className="hidden sm:flex flex-wrap items-center justify-between gap-x-7 gap-y-4 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-7 py-3.5 w-full shadow-[inset_0_1px_0_rgba(255,255,255,0.10),inset_0_-1px_0_rgba(0,0,0,0.25),0_10px_30px_rgba(0,0,0,0.35)]"
                    >
                      <a
                        href={TRUSTPILOT_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                      >
                        <div className="bg-green-500/20 p-1.5 rounded-full">
                          <SiTrustpilot className="w-4 h-4 text-green-400" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wider text-foreground/60 font-bold">
                            Trustpilot
                          </span>
                          <span className="text-xs font-bold">
                            Excellent · 4.8 / 5
                          </span>
                        </div>
                      </a>
                      <div className="w-px h-8 bg-white/10 hidden sm:block"></div>
                      <a
                        href={GOOGLE_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                      >
                        <div className="bg-white/10 p-1.5 rounded-full">
                          <FcGoogle className="w-4 h-4" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wider text-foreground/60 font-bold">
                            Google Reviews
                          </span>
                          <span className="text-xs font-bold">4.9 / 5 · 220+</span>
                        </div>
                      </a>
                      <div className="w-px h-8 bg-white/10 hidden sm:block"></div>
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/20 p-1.5 rounded-full">
                          <Award className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wider text-foreground/60 font-bold">
                            Accreditation
                          </span>
                          <span className="text-xs font-bold">JCI Certified</span>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="lg:ml-auto w-full max-w-md hidden lg:block"
                  >
                    <div className="relative">
                      <div className="absolute -inset-6 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[3rem] blur-3xl pointer-events-none"></div>
                      <div className="shine-border relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[2rem] p-7 md:p-8 shadow-[0_25px_60px_rgba(0,0,0,0.5)]">
                        <ConsultationCard
                          form={form}
                          onSubmit={onSubmit}
                          formSuccess={formSuccess}
                          resetForm={() => {
                            form.reset();
                            setFormSuccess(false);
                          }}
                          onPrivacyClick={() => setPrivacyOpen(true)}
                        />
                      </div>
                    </div>
                  </motion.div>
                </div>
              </section>

              {/* AS SEEN IN — PRESS MARQUEE */}
              <section
                id="press"
                className="relative pt-0 pb-4 md:pb-6 overflow-hidden"
                aria-label="As seen in"
              >
                <div className="max-w-7xl mx-auto px-4 md:px-6 mb-2 md:mb-3">
                  <p className="text-center text-[11px] md:text-xs uppercase tracking-[0.25em] text-foreground/60 font-semibold">
                    As Featured In
                  </p>
                </div>
                <div ref={pressOuterRef} className="relative overflow-hidden touch-pan-y select-none">
                  {/* Edge fades */}
                  <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-background to-transparent z-10"></div>
                  <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-background to-transparent z-10"></div>

                  <div ref={pressInnerRef} className="flex w-max will-change-transform">
                    {[...Array(2)].map((_, dup) => (
                      <div
                        key={dup}
                        className="flex items-center gap-12 md:gap-20 px-6 md:px-10 shrink-0"
                        aria-hidden={dup === 1}
                      >
                        {[
                          { src: pressMemorial, alt: "Memorial" },
                          { src: pressWashingtonTimes, alt: "The Washington Times" },
                          { src: pressYahooFinance, alt: "Yahoo Finance" },
                          { src: pressIBT, alt: "International Business Times" },
                          { src: pressLifeStyle, alt: "Life & Style" },
                          { src: pressNYWeekly, alt: "New York Weekly" },
                          { src: pressDailyCaller, alt: "Daily Caller" },
                          { src: pressTechBullion, alt: "TechBullion" },
                          { src: pressFirst, alt: "First for Women" },
                          { src: pressBenzinga, alt: "Benzinga" },
                          { src: pressMarketsInsider, alt: "Markets Insider" },
                          { src: pressFox8, alt: "Fox 8" },
                          { src: pressWikipedia, alt: "Wikipedia" },
                        ].map((logo, i) => (
                          <img
                            key={`${dup}-${i}`}
                            src={logo.src}
                            alt={logo.alt}
                            draggable={false}
                            className="h-14 md:h-20 w-auto object-contain opacity-55 hover:opacity-100 transition-opacity shrink-0 pointer-events-none"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* WHY US */}
              <section
                id="why-us"
                className="pt-6 md:pt-10 pb-10 md:pb-14 relative scroll-mt-24 surface-lighter overflow-hidden"
                style={{
                  ["--cursor-x" as never]: "50%",
                  ["--cursor-y" as never]: "50%",
                  ["--cursor-opacity" as never]: "0",
                }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--cursor-x", `${e.clientX - rect.left}px`);
                  e.currentTarget.style.setProperty("--cursor-y", `${e.clientY - rect.top}px`);
                  e.currentTarget.style.setProperty("--cursor-opacity", "1");
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.setProperty("--cursor-opacity", "0");
                }}
              >
                {/* Cursor-following spotlight blob */}
                <div
                  className="absolute pointer-events-none rounded-full z-[1]"
                  style={{
                    left: "var(--cursor-x)",
                    top: "var(--cursor-y)",
                    opacity: "var(--cursor-opacity)",
                    transform: "translate(-50%, -50%)",
                    width: "560px",
                    height: "560px",
                    background:
                      "radial-gradient(circle, rgba(230,220,200,0.18), rgba(230,220,200,0.05) 40%, transparent 70%)",
                    filter: "blur(70px)",
                    transition: "opacity 350ms ease",
                    willChange: "transform, opacity",
                  }}
                />
                {/* Premium VIP background — vignette: edges visible, center darkened so cards stay legible */}
                <div className="absolute inset-0 pointer-events-none">
                  <img
                    src={whyusLuxuryBg}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-70"
                  />
                  {/* Lighter radial darken-center vignette so cards stay legible while image stays visible */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "radial-gradient(ellipse 55% 60% at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.05) 80%, transparent 100%)",
                    }}
                  ></div>
                  {/* Subtle top darken for nav seam */}
                  <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent"></div>
                  {/* Bottom dark fade extending up to half the section */}
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
                </div>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(200,165,110,0.08),transparent_60%)] pointer-events-none"></div>
                <div className="container mx-auto px-6 relative">
                  <SectionHeader
                    eyebrow="01 — The Standard"
                    uppercase
                    divider
                    title={
                      <>
                        Why Men <span className="gold-shimmer-inner">Choose Us</span>
                      </>
                    }
                    subtitle="Every detail engineered for the most demanding standard — yours."
                  />

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-5 max-w-7xl mx-auto mt-8 sm:mt-14">
                    {[
                      {
                        num: "01",
                        Icon: Lock,
                        title: "Absolute Discretion",
                        desc: "Decisions made in private. Your journey and identity are protected by strict medical confidentiality protocols.",
                        stat: "100%",
                        sub: "Confidential",
                      },
                      {
                        num: "02",
                        Icon: Stethoscope,
                        title: "Master Surgeons",
                        desc: "Board-certified urologists and microsurgeons with decades of reconstructive and aesthetic experience.",
                        stat: "25+",
                        sub: "Years Avg.",
                      },
                      {
                        num: "03",
                        Icon: UserCheck,
                        title: "Bespoke Architecture",
                        desc: "Personalized surgical plans tailored to your unique anatomical goals. No compromises. No generic approaches.",
                        stat: "1-of-1",
                        sub: "Per Patient",
                      },
                      {
                        num: "04",
                        Icon: Activity,
                        title: "Lifetime Standard",
                        desc: "Dedicated follow-up and monitoring to ensure permanent confidence. Results that last a lifetime.",
                        stat: "∞",
                        sub: "Aftercare",
                      },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        className="relative bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl p-3 sm:p-7 hover:-translate-y-1 hover:bg-white/[0.06] hover:border-primary/30 transition-all duration-500 group overflow-hidden"
                      >
                        <span className="glass-shine-border" aria-hidden></span>
                        <span className="absolute top-2 right-2 sm:top-5 sm:right-6 font-sans font-extrabold text-2xl sm:text-6xl text-white/[0.06] group-hover:text-primary/20 transition-colors leading-none">
                          {item.num}
                        </span>
                        <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center mb-2 sm:mb-6 group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                          <item.Icon className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
                        </div>
                        <h3 className="text-[11px] sm:text-base font-extrabold uppercase tracking-wide mb-1.5 sm:mb-3 leading-tight">
                          {item.title}
                        </h3>
                        <p className="hidden sm:block text-[11px] sm:text-sm text-foreground/70 font-medium leading-relaxed mb-3 sm:mb-6 sm:min-h-[88px]">
                          {item.desc}
                        </p>
                        <div className="border-t border-white/10 pt-2 sm:pt-4 flex items-baseline gap-1.5 sm:gap-2">
                          <span className="text-base sm:text-2xl font-extrabold text-primary tracking-tight">
                            {item.stat}
                          </span>
                          <span className="text-[8px] sm:text-[10px] uppercase tracking-widest text-foreground/55 font-bold">
                            {item.sub}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="flex justify-center mt-6 sm:mt-10">
                    <Button
                      onClick={openConsultation}
                      className="relative overflow-hidden cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 h-12 sm:h-14 px-8 sm:px-10 rounded-full font-bold tracking-[0.2em] uppercase text-xs sm:text-sm shadow-[0_0_25px_rgba(200,165,110,0.35)] group/cta"
                    >
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-y-0 -left-full w-full -skew-x-12 opacity-0 group-hover/cta:opacity-100 group-hover/cta:translate-x-[220%] transition-all duration-[1000ms] ease-out"
                        style={{
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 35%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.15) 65%, transparent 100%)",
                        }}
                      />
                      <span className="relative z-10 gold-shimmer-soft">Choose Us</span>
                    </Button>
                  </div>

                  {/* Numbers grid (merged into The Standard section) */}
                  <div
                    id="numbers"
                    className="scroll-mt-24 mt-6 md:mt-10 grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap md:items-center md:justify-around gap-y-8 md:gap-y-10 max-w-6xl mx-auto"
                  >
                    {[
                      { value: 12000, suffix: "+", display: "12,000+", label: "Procedures" },
                      { value: 60, suffix: "+", display: "60+", label: "Countries Served" },
                      { value: 25, suffix: "+", display: "25+", label: "Years Experience" },
                      { value: 4.9, suffix: "/5", display: "4.9/5", label: "Patient Rating", decimals: 1 },
                      { value: 24, suffix: "/7", display: "24/7", label: "Multilingual Care" },
                    ].map((stat, i, arr) => (
                      <React.Fragment key={i}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.4 }}
                          transition={{ duration: 0.5, delay: i * 0.08 }}
                          className={`relative flex flex-col items-center text-center px-2 md:px-4 md:min-w-[140px] group ${i === arr.length - 1 ? "col-span-2 sm:col-span-3 md:col-auto" : ""
                            }`}
                        >
                          <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2 md:mb-3 leading-none tracking-tight gold-shimmer-light">
                            <CountUp
                              end={stat.value}
                              suffix={stat.suffix}
                              decimals={stat.decimals ?? 0}
                              formatThousands={stat.value >= 1000}
                            />
                          </div>
                          <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.25em] text-foreground/70 font-bold">
                            {stat.label}
                          </div>
                        </motion.div>
                        {i < arr.length - 1 && (
                          <div className="hidden md:block w-px h-16 bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </section>

              {/* PROCEDURES */}
              <section
                id="procedures"
                className="py-20 md:py-24 surface-deep relative scroll-mt-24 overflow-hidden"
              >
                {/* Bleed from why-us above — soft top fade so the two sections merge */}
                <div className="absolute inset-x-0 -top-px h-40 bg-gradient-to-b from-black/70 via-black/35 to-transparent pointer-events-none z-[1]"></div>
                {/* High-contrast accent washes for premium card pop */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(200,165,110,0.12),transparent_55%)] pointer-events-none"></div>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(200,165,110,0.08),transparent_55%)] pointer-events-none"></div>
                <div className="container mx-auto px-6 relative z-10">
                  <SectionHeader
                    eyebrow="02 — Procedures"
                    title={
                      <>
                        Signature <span className="text-primary">Treatments</span>
                      </>
                    }
                    subtitle="Four flagship procedures, each performed by senior surgeons with international training."
                    subtitleClassName="text-[11px] md:text-base max-w-md md:max-w-none mx-auto"
                  />

                  <div className="grid grid-cols-2 gap-3 md:gap-6 mt-10 md:mt-16">
                    <ProcedureCard
                      img={img1}
                      num="01"
                      title="Penile Curvature Correction"
                      desc="Surgical straightening for curvature caused by Peyronie's disease or congenital conditions, restoring both function and natural appearance."
                      features={[
                        "Corrects curvature",
                        "Improves function",
                        "Restores aesthetics",
                        "Eliminates discomfort",
                      ]}
                      openWhatsApp={openWhatsApp}
                      openConsultation={openConsultation}
                    />
                    <ProcedureCard
                      img={img2}
                      num="02"
                      title="Enlargement with Dermal Graft"
                      desc="A soft-tissue dermal graft adds both length and girth. The graft integrates over time for natural, permanent enhancement and lasting results."
                      features={[
                        "Soft-tissue graft",
                        "Permanent integration",
                        "Length & girth",
                        "Natural feel",
                      ]}
                      openWhatsApp={openWhatsApp}
                      openConsultation={openConsultation}
                    />
                    <ProcedureCard
                      img={img3}
                      num="03"
                      title="Penile Prosthesis (Implant)"
                      desc="A reliable solution for erectile dysfunction when other treatments fall short. Achieve a firm, natural-looking erection on demand, every time."
                      features={[
                        "On-demand control",
                        "Natural look & feel",
                        "Long-term reliability",
                        "Restored confidence",
                      ]}
                      openWhatsApp={openWhatsApp}
                      openConsultation={openConsultation}
                    />
                    <ProcedureCard
                      img={img4}
                      num="04"
                      title="Enlargement with Fat Transfer"
                      desc="A minimally invasive technique using your own purified fat cells to enhance girth — natural-feeling results with a short recovery window."
                      features={[
                        "Own purified fat",
                        "Minimally invasive",
                        "Enhanced girth",
                        "Short recovery",
                      ]}
                      openWhatsApp={openWhatsApp}
                      openConsultation={openConsultation}
                    />
                  </div>

                  {/* Inline divider — Lead Surgeon now collapsed inside Procedures */}
                  <div className="mt-10 md:mt-16 pt-8 md:pt-12 border-t border-white/10">
                    <div className="text-center mb-6 md:mb-10">
                      <div className="inline-block border border-white/10 rounded-full px-4 py-1.5 bg-white/[0.03] backdrop-blur-sm">
                        <span className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
                          Performed by your Lead Surgeon
                        </span>
                      </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-6 md:gap-10 lg:gap-16 items-center">
                      {/* Doctor Image */}
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-5 relative"
                      >
                        <DoctorImageCard
                          drAlpha={drAlpha}
                          onPlay={() => setVideoOpen(true)}
                        />
                      </motion.div>

                      {/* Doctor Info */}
                      <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-7"
                      >
                        <div className="mb-5 md:mb-7">
                          <div className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-primary/85 font-extrabold mb-2">
                            Known Internationally As
                          </div>
                          <span className="gold-shimmer text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                            Dr. Alpha
                          </span>
                        </div>

                        <p className="text-xs md:text-lg text-foreground/85 leading-relaxed mb-5 md:mb-7 font-medium">
                          Professor of Urology at Memorial Bahçelievler Hospital with
                          <strong className="text-foreground"> 25+ years</strong> in
                          andrology, reconstructive urology and aesthetic genital
                          surgery — one of the most referred surgeons internationally
                          for penile prosthesis, curvature correction and male
                          enhancement.
                        </p>

                        {/* Credential bullets */}
                        <div className="grid grid-cols-2 gap-2 md:gap-3 mb-6 md:mb-10">
                          {[
                            { Icon: GraduationCap, text: "Professor of Urology" },
                            { Icon: Award, text: "25+ years operative practice" },
                            { Icon: BookOpen, text: "100+ peer-reviewed papers" },
                            { Icon: Users, text: "12,000+ patients treated" },
                          ].map((c, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 md:gap-3 bg-white/[0.04] border border-white/10 rounded-xl px-2.5 md:px-4 py-2 md:py-3"
                            >
                              <div className="w-7 h-7 md:w-9 md:h-9 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0">
                                <c.Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                              </div>
                              <span className="text-[11px] md:text-sm font-bold text-foreground/90 leading-tight">
                                {c.text}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row gap-3">
                          <Button
                            className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 rounded-full font-bold tracking-wide uppercase text-sm shadow-[0_0_25px_rgba(200,165,110,0.3)]"
                            onClick={openConsultation}
                          >
                            Book Private Consultation
                          </Button>
                          <Button
                            variant="outline"
                            className="border-white/20 hover:bg-white/5 h-14 px-8 rounded-full font-bold tracking-wide uppercase text-sm bg-white/[0.03]"
                            onClick={openWhatsApp}
                          >
                            <FaWhatsapp className="mr-2 w-5 h-5 text-green-400" />
                            Message on WhatsApp
                          </Button>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </section>

              {/* PERSONALIZED PLAN — standalone section 04 */}
              <section
                id="plan"
                className="pt-12 md:pt-24 pb-12 md:pb-24 relative overflow-hidden scroll-mt-24 surface-light"
              >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(200,165,110,0.08),transparent_60%)] pointer-events-none"></div>
                <div className="container mx-auto px-3 md:px-6 relative z-10">
                  <PersonalizedPlanSection
                    drAlpha={drAlpha}
                    onBook={openConsultation}
                    onWhatsApp={openWhatsApp}
                  />
                </div>
              </section>

              {/* ISTANBUL + JOURNEY (merged) — section 06 */}
              <section
                id="istanbul"
                className="relative min-h-[90vh] flex items-center py-10 md:py-14 overflow-hidden scroll-mt-24"
              >
                <div className="absolute inset-x-0 top-0 h-[55dvh] md:inset-0 md:h-auto z-0 bg-background pointer-events-none overflow-hidden">
                  <video
                    ref={istanbulVideoRef}
                    src="https://player.vimeo.com/progressive_redirect/playback/1187967599/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=7802ae14eff2a4c084828b4f28c0f6fea2c90272da2050a0bb3a62344642a1ba"
                    className="absolute inset-0 w-full h-full object-cover opacity-95 pointer-events-none"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    disablePictureInPicture
                    controls={false}
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  {/* Soft side + bottom blend — keeps the video bright while letting type read */}
                  <div className="absolute inset-0 z-10 bg-gradient-to-r from-background/65 via-background/10 to-transparent pointer-events-none"></div>
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-background via-background/40 to-transparent pointer-events-none"></div>
                  {/* Brief fading center scrim — hides any initial play overlay while autoplay starts, then disappears */}
                  <div className="absolute inset-0 z-20 pointer-events-none yt-play-suppress"></div>
                </div>

                <div className="container mx-auto px-6 relative z-20">
                  <div className="max-w-2xl mb-8">
                    <div className="inline-block border border-white/15 rounded-full px-4 py-1 bg-white/[0.05] backdrop-blur-md mb-4">
                      <span className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
                        04 — The Destination
                      </span>
                    </div>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-5 text-white leading-[1.05] tracking-tight">
                      Why <span className="text-primary">Istanbul</span>?
                    </h2>
                    <div className="text-white/90 text-base md:text-lg font-medium leading-relaxed">
                      <p>
                        The world's premier hub for advanced medical procedures —
                        elite European standards with discreet, high-end care.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {[
                      { stat: "1.5M+", label: "Medical Tourists per Year" },
                      { stat: "Top 10", label: "Global Medical Hub" },
                      { stat: "JCI", label: "Highest International Standard" },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className={`bg-black/50 backdrop-blur-xl border border-white/15 rounded-2xl p-4 md:p-5 hover:-translate-y-1 hover:border-primary/40 transition-all duration-500 ${i === 2 ? "col-span-2 md:col-span-1" : ""
                          }`}
                      >
                        <div className="text-2xl md:text-3xl font-extrabold text-primary mb-1">
                          {item.stat}
                        </div>
                        <div className="text-white/80 uppercase tracking-widest text-[10px] font-bold">
                          {item.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* JOURNEY PHASES — collapsed into Destination */}
                  <div className="mt-10">
                    <div className="max-w-2xl mb-6">
                      <h3 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-5">
                        Your <span className="text-primary">Journey</span>
                      </h3>
                      <p className="text-white/90 text-base md:text-lg font-medium leading-relaxed">
                        Five orchestrated phases, from first private message to lifetime aftercare.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                      {JOURNEY_PHASES.map((phase, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.3 }}
                          transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                          className={`relative bg-black/40 backdrop-blur-xl border border-white/15 rounded-2xl p-4 hover:-translate-y-1 hover:bg-black/55 hover:border-primary/40 transition-all duration-500 group overflow-hidden ${i === JOURNEY_PHASES.length - 1 ? "col-span-2 lg:col-span-1" : ""
                            }`}
                        >
                          <span className="absolute top-3 right-4 font-sans font-extrabold text-4xl text-white/[0.06] group-hover:text-primary/20 transition-colors leading-none">
                            {phase.num}
                          </span>
                          <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/15 flex items-center justify-center mb-3 group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                            <phase.Icon className="w-4 h-4 text-primary" />
                          </div>
                          <h4 className="text-xs font-extrabold uppercase tracking-wide mb-1.5 leading-tight text-white">
                            {phase.title}
                          </h4>
                          <p className="text-[11px] text-white/70 font-medium leading-relaxed mb-3 min-h-[44px]">
                            {phase.desc}
                          </p>
                          <div className="border-t border-white/10 pt-2 flex items-baseline gap-2">
                            <span className="text-lg font-extrabold text-primary tracking-tight">
                              {phase.stat}
                            </span>
                            <span className="text-[9px] uppercase tracking-widest text-white/55 font-bold">
                              {phase.sub}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="flex justify-center mt-12 md:mt-16">
                      <Button
                        onClick={openConsultation}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-10 rounded-full font-bold tracking-[0.25em] uppercase text-sm shadow-[0_0_30px_rgba(200,165,110,0.4)]"
                      >
                        Start
                      </Button>
                    </div>
                  </div>
                </div>
              </section>

              {/* HOSPITALS */}
              <section
                id="hospitals"
                className="py-20 md:py-24 relative scroll-mt-24 surface-deep overflow-hidden"
              >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(200,165,110,0.07),transparent_60%)] pointer-events-none"></div>
                <div className="container mx-auto px-6 relative">
                  <SectionHeader
                    eyebrow="05 — Hospital Network"
                    title={<>Memorial Hospitals Group</>}
                    titleClassName="text-2xl md:text-5xl lg:text-6xl whitespace-nowrap"
                    subtitle="Procedures performed inside JCI-accredited Memorial hospitals — Turkey's largest private healthcare network."
                    subtitleClassName="text-[11px] md:text-base max-w-md md:max-w-2xl mx-auto"
                  />

                  {/* Single-row horizontal marquee — JS-driven, drag-to-scrub */}
                  <div
                    ref={hospOuterRef}
                    className="mt-16 mb-12 overflow-hidden select-none touch-pan-y"
                    style={{
                      maskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
                      WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
                      touchAction: "pan-y",
                    }}
                  >
                    <div ref={hospInnerRef} className="flex gap-2.5 md:gap-5 w-max will-change-transform">
                      {[
                        { name: "Memorial Bahçelievler", loc: "Istanbul", tag: "Flagship · Urology Center", flag: true, img: hospBahcelievler },
                        { name: "Memorial Şişli", loc: "Istanbul", tag: "Robotic Surgery", img: hospSisli },
                        { name: "Memorial Bodrum", loc: "Bodrum", tag: "Aegean Recovery Resort", img: hospBodrum },
                        { name: "Memorial Göztepe", loc: "Istanbul", tag: "Ambulatory Surgery", img: hospGoztepe },
                        { name: "Memorial Ataşehir", loc: "Istanbul", tag: "Premium Outpatient", img: hospAtasehir },
                        { name: "Memorial Ankara", loc: "Ankara", tag: "Capital Center", img: hospAnkara },
                        { name: "Memorial Bahçelievler", loc: "Istanbul", tag: "Flagship · Urology Center", flag: true, img: hospBahcelievler },
                        { name: "Memorial Şişli", loc: "Istanbul", tag: "Robotic Surgery", img: hospSisli },
                        { name: "Memorial Bodrum", loc: "Bodrum", tag: "Aegean Recovery Resort", img: hospBodrum },
                        { name: "Memorial Göztepe", loc: "Istanbul", tag: "Ambulatory Surgery", img: hospGoztepe },
                        { name: "Memorial Ataşehir", loc: "Istanbul", tag: "Premium Outpatient", img: hospAtasehir },
                        { name: "Memorial Ankara", loc: "Ankara", tag: "Capital Center", img: hospAnkara },
                      ].map((hosp, i) => (
                        <div
                          key={i}
                          className={`flex-shrink-0 w-[10rem] md:w-72 relative bg-white/[0.04] backdrop-blur-xl border rounded-2xl group hover:-translate-y-1 hover:bg-white/[0.06] transition-all duration-500 overflow-hidden ${hosp.flag ? "border-primary/40" : "border-white/10 hover:border-primary/30"
                            }`}
                        >
                          <div className="relative h-24 md:h-44 overflow-hidden bg-[#0d0d0d]">
                            <img
                              src={hosp.img}
                              alt={hosp.name}
                              loading="lazy"
                              draggable={false}
                              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 select-none pointer-events-none"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"></div>
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            {hosp.flag && (
                              <span className="absolute top-2 left-2 md:top-4 md:left-4 text-[8px] md:text-[9px] font-extrabold tracking-[0.2em] uppercase bg-primary text-primary-foreground rounded-full px-2 md:px-2.5 py-0.5 md:py-1 shadow-lg z-10">
                                Flagship
                              </span>
                            )}
                            <div className="absolute bottom-2 left-2 md:bottom-3 md:left-4 flex items-center gap-1.5 text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-white font-extrabold drop-shadow z-10">
                              <MapPin className="w-3 h-3" />
                              {hosp.loc}
                            </div>
                          </div>
                          <div className="p-3 md:p-5 min-h-[68px] md:min-h-[88px] flex items-start">
                            <h3 className="text-sm md:text-lg font-extrabold tracking-tight leading-tight">
                              {(() => {
                                const parts = hosp.name.split(" ");
                                return (
                                  <>
                                    {parts[0]}
                                    <br />
                                    {parts.slice(1).join(" ")}
                                  </>
                                );
                              })()}
                            </h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* JCI Accreditation — collapsed into Hospital Network */}
                  <div className="mt-14 pt-12 border-t border-white/10">
                    <div className="grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-14 items-center max-w-5xl mx-auto">
                      <div className="flex justify-center">
                        <CertificateMedal />
                      </div>

                      <div>
                        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-primary/85 font-extrabold mb-3">
                          Accredited Excellence
                        </div>
                        <h3 className="text-2xl md:text-3xl font-extrabold mb-3 leading-[1.05] tracking-tight">
                          Gold-Seal{" "}
                          <span className="text-primary">Joint Commission</span>{" "}
                          Accreditation
                        </h3>
                        <p className="text-base text-foreground/85 font-medium leading-relaxed mb-4">
                          Every procedure is performed inside JCI-accredited hospitals — the world's most rigorous patient-safety standard.
                        </p>
                        <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
                          {[
                            "Audited surgical safety protocols",
                            "US/EU sterile-environment standards",
                          ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <div className="w-4 h-4 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-2.5 h-2.5 text-primary" />
                              </div>
                              <span className="text-sm font-medium text-foreground/90">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                </div>
              </section>

              {/* FAQ + PATIENT STORIES — full-screen split, unified section header */}
              <section id="faq" className="py-10 md:py-20 relative scroll-mt-24 surface-deep overflow-hidden">
                <div className="container mx-auto px-6 w-full">
                  <SectionHeader
                    eyebrow="06 — Answers & Stories"
                    title={<>Clear <span className="text-primary">Answers</span>,<br />Real <span className="text-primary">Results</span></>}
                    align="left"
                    titleClassName="text-3xl md:text-5xl lg:text-6xl"
                  />
                  <div className="text-foreground/70 text-xs md:text-base mt-3 mb-6 md:mb-10 max-w-2xl">
                    Everything you need to know — alongside verified accounts from international patients. Identities anonymized.
                  </div>

                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-stretch">

                    {/* LEFT — FAQ */}
                    <div className="flex flex-col" ref={faqColRef} id="stories-anchor">
                      <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl p-4 md:p-8">
                        <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-2">
                          <FAQItem
                            value="faq-1"
                            q="Is the procedure and consultation completely discreet?"
                            a="Absolute discretion is the foundation of our clinic. From encrypted communications to private entrances and exclusive recovery suites, your identity and procedure details are protected by stringent medical confidentiality protocols at all times."
                          />
                          <FAQItem
                            value="faq-2"
                            q="How long is the recovery period?"
                            a="Recovery varies by procedure, but most patients return to non-strenuous daily activities within 3 to 5 days. Sexual activity and heavy exercise typically require a 4 to 6 week abstention period. Detailed timelines are provided during consultation."
                          />
                          <FAQItem
                            value="faq-3"
                            q="Do you cater to international patients?"
                            a="Yes. Over 80% of our clients fly in from abroad. We provide a full VIP concierge protocol including luxury accommodation, discreet private transfers, and a dedicated multilingual medical coordinator available 24/7."
                          />
                          <FAQItem
                            value="faq-4"
                            q="What type of anesthesia is used?"
                            a="Procedures are performed under general anesthesia or deep twilight sedation administered by specialized anesthesiologists. We use advanced European protocols to minimize grogginess and ensure a smooth, pain-free recovery."
                          />
                          <FAQItem
                            value="faq-5"
                            q="Are the results permanent?"
                            a="Yes. Our surgical interventions — whether structural correction, dermal grafting, or prosthesis implantation — are designed for lifetime permanence. We do not offer temporary filler solutions."
                          />
                          <FAQItem
                            value="faq-6"
                            q="What are the risks involved?"
                            a="As with all surgery, risks exist. However, our JCI-accredited facilities and strictly senior-surgeon-only policy reduce these to absolute minimums. A full, transparent medical risk assessment is standard protocol before any commitment."
                          />
                        </Accordion>
                      </div>

                      <div className="bg-primary/[0.08] border border-primary/20 rounded-2xl md:rounded-3xl p-4 md:p-7 backdrop-blur-sm mt-4 md:mt-6 flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 justify-between">
                        <p className="text-xs md:text-base font-medium text-foreground/85 leading-relaxed flex-1">
                          Have a specific question? Our medical coordinators are available 24/7.
                        </p>
                        <Button
                          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11 md:h-12 px-5 md:px-6 uppercase tracking-wider text-xs font-bold shadow-[0_0_20px_rgba(200,165,110,0.3)] flex-shrink-0"
                          onClick={openWhatsApp}
                        >
                          <FaWhatsapp className="mr-2 w-4 h-4" /> Ask on WhatsApp
                        </Button>
                      </div>
                    </div>

                    {/* RIGHT — Patient Stories (infinite vertical marquee, exactly 6 visible) */}
                    <div
                      id="stories"
                      className="flex flex-col lg:justify-center scroll-mt-24 overflow-hidden"
                      style={isDesktop && faqHeight ? { height: faqHeight } : undefined}
                    >
                      {/* Stories marquee — mobile: 4 cards × 104 + 3 gaps × 8 = 440px; desktop: 6 × 104 + 5 × 8 = 664px */}
                      <div
                        className="relative overflow-hidden h-[440px] lg:h-[664px]"
                        style={{
                          maskImage:
                            "linear-gradient(to bottom, transparent 0%, black 18%, black 92%, transparent 100%)",
                          WebkitMaskImage:
                            "linear-gradient(to bottom, transparent 0%, black 18%, black 92%, transparent 100%)",
                        }}
                      >
                        <div className="marquee-up flex flex-col gap-2">
                          {[...PATIENT_STORIES, ...PATIENT_STORIES].map((story, i) => (
                            <article
                              key={i}
                              className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl px-3 py-2.5 flex flex-col justify-center gap-1.5 hover:border-primary/30 transition-colors relative overflow-hidden flex-shrink-0 h-[104px]"
                            >
                              <div className="text-primary text-4xl font-serif absolute top-0 right-3 opacity-10 leading-none pointer-events-none select-none">
                                "
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full border-2 border-primary/40 flex-shrink-0 overflow-hidden">
                                  <img
                                    src={story.img}
                                    alt={`Patient ${story.initials}`}
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-extrabold tracking-wide leading-tight">
                                      {story.initials}
                                    </span>
                                    <MdVerified className="w-2.5 h-2.5 text-primary flex-shrink-0" />
                                  </div>
                                  <div className="text-[8px] text-foreground/60 uppercase tracking-[0.18em] font-bold mt-0.5 truncate">
                                    {story.demo}
                                  </div>
                                </div>
                                <div className="flex items-center gap-0.5">
                                  {Array.from({ length: 5 }).map((_, s) => (
                                    <Star key={s} className="w-2 h-2 fill-primary text-primary" />
                                  ))}
                                </div>
                              </div>
                              <p className="text-foreground/80 font-medium leading-snug text-[11px] md:text-[12px] relative z-10 line-clamp-3">
                                "{story.quote}"
                              </p>
                            </article>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </section>

            </main>

            {/* FOOTER — minimal */}
            <footer className="bg-card border-t border-white/5 pt-14 pb-8">
              <div className="container mx-auto px-6 max-w-6xl">
                {/* Brand row */}
                <div className="flex flex-col items-center text-center mb-10">
                  <img src={lightLogo} alt="rememore" className="h-7" />
                </div>

                {/* Action card buttons — linear rounded */}
                <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
                  >
                    <FaWhatsapp className="w-3.5 h-3.5 text-green-400" />
                    WhatsApp
                  </a>
                  <a
                    href={PHONE_LINK}
                    className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
                  >
                    <Phone className="w-3.5 h-3.5 text-primary" />
                    Call
                  </a>
                  <a
                    href={EMAIL_LINK}
                    className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
                  >
                    <Mail className="w-3.5 h-3.5 text-primary" />
                    Email
                  </a>
                  <a
                    href={GOOGLE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
                  >
                    <FcGoogle className="w-3.5 h-3.5" />
                    Google
                  </a>
                  <a
                    href={TRUSTPILOT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
                  >
                    <SiTrustpilot className="w-3.5 h-3.5 text-green-400" />
                    Trustpilot
                  </a>
                </div>

                {/* Section nav — minimal underline links */}
                <div className="flex flex-wrap justify-center gap-x-7 gap-y-2 mb-10">
                  {NAV_LINKS.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => scrollTo(link.id)}
                      className="text-[11px] uppercase tracking-[0.25em] font-bold text-foreground/55 hover:text-primary transition-colors"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>

                {/* Bottom spacer — preserves bottom margin so the sticky-bar fade has clean breathing room */}
                <div className="md:hidden h-24" aria-hidden></div>
                <p className="text-[11px] text-center text-[#646F79] my-5">This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.</p>
              </div>
            </footer>

            {/* Floating WhatsApp Bubble (Desktop) */}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex fixed bottom-8 right-8 w-16 h-16 rounded-full items-center justify-center text-white shadow-[0_0_30px_rgba(34,197,94,0.5)] hover:scale-110 transition-transform z-50 bg-[#218f62]"
              aria-label="Chat on WhatsApp"
            >
              <FaWhatsapp className="w-8 h-8" />
            </a>

            {/* Mobile sticky-bar backdrop fade — sits behind the sticky bar so page content blurs into the bar */}
            <div
              aria-hidden
              className={`md:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none h-32 transition-opacity duration-300 ${isNavOpen ? "opacity-0" : "opacity-100"
                }`}
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 35%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0) 100%)",
                WebkitMaskImage:
                  "linear-gradient(to top, black 0%, black 60%, transparent 100%)",
                maskImage:
                  "linear-gradient(to top, black 0%, black 60%, transparent 100%)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
              }}
            />

            {/* Mobile Sticky Action Bar — hidden when mobile nav is open */}
            <div
              className={`md:hidden fixed left-0 right-0 z-50 px-3 pointer-events-none transition-all duration-300 ${isNavOpen ? "opacity-0 translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"
                }`}
              style={{ bottom: "max(env(safe-area-inset-bottom), 12px)" }}
              aria-hidden={isNavOpen}
            >
              <div className="relative pointer-events-auto bg-white/[0.06] backdrop-blur-xl border border-white/20 rounded-2xl flex items-center justify-between p-1 shadow-[0_10px_40px_rgba(0,0,0,0.45)] overflow-hidden">
                {/* Glass top highlight */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent"></div>
                {/* Subtle inner glow */}
                <div className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.10),inset_0_-1px_0_rgba(0,0,0,0.20)]"></div>

                <a
                  href={PHONE_LINK}
                  className="relative flex-1 flex items-center justify-center gap-1.5 py-2 text-foreground/85 hover:text-white"
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span className="text-[10px] uppercase tracking-wider font-bold">
                    Call
                  </span>
                </a>
                <div className="w-px h-5 bg-white/10"></div>
                <a
                  href={EMAIL_LINK}
                  className="relative flex-1 flex items-center justify-center gap-1.5 py-2 text-foreground/85 hover:text-white"
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span className="text-[10px] uppercase tracking-wider font-bold">
                    Email
                  </span>
                </a>
                <div className="w-px h-5 bg-white/10"></div>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative flex-1 flex items-center justify-center gap-1.5 py-2 text-green-400 hover:text-green-300"
                >
                  <FaWhatsapp className="w-4 h-4" />
                  <span className="green-shimmer text-[10px] uppercase tracking-wider font-bold">
                    WhatsApp
                  </span>
                </a>
              </div>
            </div>

            {/* Consultation Form Popup */}
            <ConsultationDialog
              open={formOpen}
              onOpenChange={(o) => {
                setFormOpen(o);
                if (!o) setTimeout(() => setFormSuccess(false), 400);
              }}
              form={form}
              onSubmit={onSubmit}
              formSuccess={formSuccess}
              resetForm={() => {
                form.reset();
                setFormSuccess(false);
              }}
              onPrivacyClick={() => setPrivacyOpen(true)}
            />

            {/* Privacy Notice Popup */}
            <PrivacyDialog
              open={privacyOpen}
              onOpenChange={setPrivacyOpen}
              onAccept={() =>
                form.setValue("consent", true, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                })
              }
            />

            {/* Doctor Video Popup */}
            <DoctorVideoDialog open={videoOpen} onOpenChange={setVideoOpen} />
          </div>
        </GoogleReCaptchaProvider>
      );
    }

/* ----------------- Doctor Image Card with 3D tilt + corner shines ----------------- */

function DoctorImageCard({
      drAlpha,
      onPlay,
    }: {
      drAlpha: string;
      onPlay: () => void;
    }) {
      const tiltRef = useRef<HTMLDivElement>(null);
      const glowRef = useRef<HTMLDivElement>(null);
      const hoverVideoRef = useRef<HTMLVideoElement>(null);
      const rafRef = useRef<number | null>(null);
      const pendingRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

      useEffect(() => {
        return () => {
          if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
      }, []);

      const playHoverVideo = () => {
        const v = hoverVideoRef.current;
        if (!v) return;
        if (v.paused) {
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => { });
        }
      };
      const pauseHoverVideo = () => {
        const v = hoverVideoRef.current;
        if (!v) return;
        v.pause();
        try {
          v.currentTime = 0;
        } catch {
          /* ignore */
        }
      };

      const apply = () => {
        rafRef.current = null;
        const data = pendingRef.current;
        if (!data) return;
        if (tiltRef.current) {
          tiltRef.current.style.transform = `rotateY(${data.x}deg) rotateX(${data.y}deg)`;
        }
        if (glowRef.current) {
          glowRef.current.style.background = `radial-gradient(circle at ${data.px}% ${data.py}%, rgba(200,165,110,0.30), transparent 45%)`;
        }
      };

      const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const xPct = (e.clientX - rect.left) / rect.width;
        const yPct = (e.clientY - rect.top) / rect.height;
        pendingRef.current = {
          x: (xPct - 0.5) * 12,
          y: -(yPct - 0.5) * 12,
          px: xPct * 100,
          py: yPct * 100,
        };
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(apply);
        }
      };
      const onLeave = () => {
        pendingRef.current = { x: 0, y: 0, px: 50, py: 50 };
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(apply);
        }
        pauseHoverVideo();
      };

      return (
        <div
          className="relative aspect-[4/5] w-full max-w-[520px] mx-auto lg:mx-0"
          style={{ perspective: "1400px" }}
          onMouseMove={onMouseMove}
          onMouseLeave={onLeave}
          onMouseEnter={playHoverVideo}
        >
          <div className="absolute -inset-4 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[3rem] blur-2xl"></div>

          <div
            ref={tiltRef}
            className="relative w-full h-full transition-transform duration-300 ease-out"
            style={{
              transform: "rotateY(0deg) rotateX(0deg)",
              transformStyle: "preserve-3d",
            }}
          >
            <div
              className="absolute inset-0 rounded-[2.5rem] border border-white/15 overflow-hidden bg-[#0d0d0d] shadow-[0_30px_80px_rgba(0,0,0,0.55)] group"
              style={{ transform: "translateZ(20px)" }}
            >
              <img
                src={drAlpha}
                alt="Prof. Dr. Abdulmuttalip Şimşek"
                className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-out group-hover:opacity-0"
              />

              {/* Hover-to-play muted preview video */}
              <video
                ref={hoverVideoRef}
                src={DOCTOR_VIDEO_URL}
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover object-top opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100 pointer-events-none"
              />

              {/* Mouse-following light glow */}
              <div
                ref={glowRef}
                className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(200,165,110,0.30), transparent 45%)",
                }}
              ></div>

              {/* Corner shines */}
              <div className="absolute top-0 left-0 w-24 h-24 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.18),_transparent_70%)] pointer-events-none"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-[radial-gradient(circle_at_top_right,_rgba(200,165,110,0.22),_transparent_70%)] pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-[radial-gradient(circle_at_bottom_left,_rgba(200,165,110,0.18),_transparent_70%)] pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.12),_transparent_70%)] pointer-events-none"></div>

              {/* Diagonal sweep on hover */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -inset-y-10 -left-1/2 w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-0 group-hover:translate-x-[400%] transition-transform duration-1500 ease-out"></div>
              </div>

              {/* Play button — smaller, refined */}
              <button
                type="button"
                onClick={onPlay}
                aria-label="Play doctor introduction video"
                className="absolute inset-0 flex items-center justify-center group/play focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/60 rounded-[2.5rem]"
              >
                <span className="absolute inset-0 bg-black/10 group-hover/play:bg-black/25 transition-colors"></span>
                <span className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/95 text-primary-foreground shadow-[0_0_40px_rgba(200,165,110,0.55)] transition-transform duration-300 group-hover/play:scale-110">
                  <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping"></span>
                  <Play className="relative w-5 h-5 md:w-6 md:h-6 fill-current ml-0.5" />
                </span>
              </button>

              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-6 left-6 right-6 pointer-events-none">
                <div className="text-[10px] uppercase tracking-[0.3em] text-primary font-extrabold mb-1">
                  Lead Surgeon
                </div>
                <div className="text-white text-xl font-extrabold leading-tight drop-shadow">
                  Prof. Dr. Abdulmuttalip Şimşek
                </div>
              </div>
            </div>

            {/* Floating credential pill removed per request */}
          </div>
        </div>
      );
    }

    /* ----------------- Doctor Video Dialog ----------------- */

    const DOCTOR_VIDEO_URL =
      "https://player.vimeo.com/progressive_redirect/playback/1192509649/rendition/1440p/file.mp4%20%281440p%29.mp4?loc=external&signature=205e04f096eab7d47fd686ac834b7229bdc95ab4bffa6824784a1e6086f8b2bf";

    function DoctorVideoDialog({
      open,
      onOpenChange,
    }: {
      open: boolean;
      onOpenChange: (o: boolean) => void;
    }) {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="w-[min(92vw,420px)] max-w-[420px] border-white/10 bg-[#0a0a0a] p-0 overflow-hidden rounded-2xl">
            <DialogTitle className="sr-only">
              Prof. Dr. Abdulmuttalip Şimşek — Introduction
            </DialogTitle>
            <DialogDescription className="sr-only">
              Video introduction by Prof. Dr. Abdulmuttalip Şimşek about his approach to male aesthetic surgery.
            </DialogDescription>
            <div className="relative w-full aspect-[9/16] bg-black">
              {open && (
                <video
                  src={DOCTOR_VIDEO_URL}
                  controls
                  autoPlay
                  playsInline
                  className="absolute inset-0 w-full h-full object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    /* ----------------- CountUp ----------------- */

    function CountUp({
      end,
      duration = 1800,
      suffix = "",
      decimals = 0,
      formatThousands = false,
    }: {
      end: number;
      duration?: number;
      suffix?: string;
      decimals?: number;
      formatThousands?: boolean;
    }) {
      const [value, setValue] = useState(0);
      const [started, setStarted] = useState(false);
      const ref = useRef<HTMLSpanElement | null>(null);

      useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !started) {
                setStarted(true);
              }
            });
          },
          { threshold: 0.4 },
        );
        observer.observe(el);
        return () => observer.disconnect();
      }, [started]);

      useEffect(() => {
        if (!started) return;
        let raf = 0;
        const start = performance.now();
        const tick = (now: number) => {
          const elapsed = now - start;
          const t = Math.min(1, elapsed / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setValue(end * eased);
          if (t < 1) raf = requestAnimationFrame(tick);
          else setValue(end);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
      }, [started, end, duration]);

      const display = formatThousands
        ? Math.round(value).toLocaleString("en-US")
        : value.toFixed(decimals);

      return (
        <span ref={ref}>
          {display}
          {suffix}
        </span>
      );
    }

    /* ----------------- Journey / Process Section ----------------- */

    const JOURNEY_PHASES = [
      {
        num: "01",
        Icon: ClipboardCheck,
        title: "Private Consultation",
        desc: "Encrypted, judgement-free assessment with our lead surgeon to map your private goals.",
        stat: "30",
        sub: "Min",
      },
      {
        num: "02",
        Icon: PenSquare,
        title: "Bespoke Plan",
        desc: "A surgical plan engineered to your anatomy — with transparent fixed pricing, no surprises.",
        stat: "48",
        sub: "Hrs",
      },
      {
        num: "03",
        Icon: Plane,
        title: "Transfer Support",
        desc: "5-star hotel, private chauffeur, and a dedicated coordinator from the moment you land.",
        stat: "Day 1",
        sub: "Transfer",
      },
      {
        num: "04",
        Icon: Activity,
        title: "Surgical Excellence",
        desc: "JCI-accredited theatre, board-certified team, and 24/7 elite recovery nursing care.",
        stat: "1-3",
        sub: "Hrs",
      },
      {
        num: "05",
        Icon: CalendarClock,
        title: "Lifetime Aftercare",
        desc: "We stand by every result for life. Free check-ins and free revisions if ever required.",
        stat: "∞",
        sub: "Forever",
      },
    ];

    /* ----------------- Patient Stories ----------------- */

    const PATIENT_STORIES = [
      {
        img: patientMR,
        initials: "M.R.",
        demo: "Italy · Age 39",
        quote:
          "Months of research finally paid off. Surgical precision and follow-up care set a benchmark I didn't know existed.",
      },
      {
        img: patientDK,
        initials: "D.K.",
        demo: "Sweden · Age 47",
        quote:
          "Their honesty during consultation impressed me most — no unnecessary add-ons, only what I truly needed.",
      },
      {
        img: patientPA,
        initials: "P.A.",
        demo: "Spain · Age 35",
        quote:
          "Private suite, multilingual nurses, chef-prepared meals — every small detail was thought through.",
      },
      {
        img: patientBL,
        initials: "B.L.",
        demo: "Norway · Age 50",
        quote:
          "Back to work in five days was unimaginable. I now recommend the clinic to anyone in my circle who asks.",
      },
      {
        img: patientGV,
        initials: "G.V.",
        demo: "Netherlands · Age 42",
        quote:
          "Three weeks post-op and the only sign anything happened is the result itself. Discreet healing, dramatic outcome.",
      },
      {
        img: patientCM,
        initials: "C.M.",
        demo: "Belgium · Age 44",
        quote:
          "Treated like guests at a five-star resort, with medical care held to the very same standard.",
      },
      {
        img: patientFS,
        initials: "F.S.",
        demo: "Austria · Age 36",
        quote:
          "Their psychologist consultation before surgery settled my nerves entirely — I went into the OR calm and clear.",
      },
      {
        img: patientEJ,
        initials: "E.J.",
        demo: "Ireland · Age 48",
        quote:
          "Less than half the price of two quotes I had at home — and frankly with more experienced surgeons.",
      },
      {
        img: patientVP,
        initials: "V.P.",
        demo: "Poland · Age 41",
        quote:
          "The 3D pre-op simulation showed me exactly what to expect. Reality matched the plan within millimetres.",
      },
      {
        img: patientOT,
        initials: "O.T.",
        demo: "Denmark · Age 53",
        quote:
          "At my age I assumed I had missed the window. The surgeon proved otherwise — discreetly and with dignity.",
      },
    ];

    /* ----------------- Sub-components ----------------- */

    function SectionHeader({
      eyebrow,
      title,
      subtitle,
      divider = false,
      uppercase = false,
      align = "center",
      titleClassName,
      subtitleClassName,
    }: {
      eyebrow: string;
      title: React.ReactNode;
      subtitle?: string;
      divider?: boolean;
      uppercase?: boolean;
      align?: "center" | "left";
      titleClassName?: string;
      subtitleClassName?: string;
    }) {
      const isLeft = align === "left";
      return (
        <div className={isLeft ? "text-left max-w-2xl" : "text-center max-w-3xl mx-auto"}>
          <div className="inline-block border border-white/10 rounded-full px-4 py-1.5 bg-white/[0.03] backdrop-blur-sm mb-4 md:mb-6">
            <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-primary font-bold">
              {eyebrow}
            </span>
          </div>
          <h2
            className={`font-extrabold leading-[1.05] tracking-tight mb-3 md:mb-5 ${uppercase ? "uppercase tracking-[0.02em]" : ""
              } ${titleClassName ?? "text-3xl md:text-5xl lg:text-6xl"}`}
          >
            {title}
          </h2>
          {divider && (
            <div className={`${isLeft ? "" : "mx-auto"} w-16 h-[3px] rounded-full bg-primary mb-6`}></div>
          )}
          {subtitle && (
            <p className={`text-foreground/75 font-medium leading-relaxed ${subtitleClassName ?? "text-xs md:text-base"}`}>
              {subtitle}
            </p>
          )}
        </div>
      );
    }

    function ConsultationCard({
      form,
      onSubmit,
      formSuccess,
      resetForm,
      onPrivacyClick,
    }: {
      form: ReturnType<typeof useForm<FormValues>>;
      onSubmit: (v: FormValues) => void | Promise<void>;
      formSuccess: boolean;
      resetForm: () => void;
      onPrivacyClick?: () => void;
    }) {
      return (
        <div className="bg-transparent relative">
          <div className="relative z-10">
            {formSuccess ? (
              <SuccessScreen onReset={resetForm} />
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1.5">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  <span className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
                    Private & Confidential
                  </span>
                </div>
                <h3 className="font-extrabold text-2xl md:text-3xl mb-1.5 tracking-tight">
                  Request a <span className="text-primary">Consultation</span>
                </h3>
                <p className="text-sm text-foreground/65 mb-5 font-medium text-balance">
                  A senior advisor will respond personally within 24h.
                </p>
                
                <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="space-y-3"
                    >
                      <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder="Full Name"
                                className="bg-black/25 border-white/10 h-11 rounded-xl focus-visible:ring-primary/50 text-base font-medium"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder="Email Address"
                                type="email"
                                className="bg-black/25 border-white/10 h-11 rounded-xl focus-visible:ring-primary/50 text-base font-medium"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Phone: flag-based international input with country validation */}
                      <FormField
                        control={form.control}
                        name="phone"
                        render={() => (
                          <FormItem>
                            <PhoneField
                              defaultCountry="gb"
                              placeholder="Phone Number"
                              invalid={!!form.formState.errors.phone}
                              initialValue={form.watch("phone")}
                              className="bg-black/25 border-white/10 h-11 rounded-xl focus-visible:ring-primary/50 text-base font-medium"
                              onChange={(d) => {
                                form.setValue("phone", d.number, {
                                  shouldValidate: form.formState.isSubmitted,
                                });
                                form.setValue("phonePrefix", d.dialCode);
                                form.setValue("phoneCountry", d.country);
                                form.setValue("phoneValid", d.valid, {
                                  shouldValidate: form.formState.isSubmitted,
                                });
                              }}
                              onBlur={() => {
                                if (form.formState.isSubmitted) form.trigger("phone");
                              }}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="consent"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-2.5 space-y-0 pt-0.5">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                className="border-white/25 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground mt-0.5 rounded"
                              />
                            </FormControl>
                            <div className="space-y-0 leading-none">
                              <FormLabel className="text-[10px] text-foreground/65 font-medium leading-snug">
                                I agree to the{" "}
                                <button
                                  type="button"
                                  onClick={onPrivacyClick}
                                  className="font-bold text-primary hover:text-primary/80 underline-offset-2 hover:underline cursor-pointer"
                                >
                                  Privacy Notice
                                </button>{" "}
                                and consent to be contacted regarding my consultation request.
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />

                      <div className="pt-1 px-1 -mx-1">
                        <RecaptchaSubmitButton
                          isSubmitting={form.formState.isSubmitting}
                          onTokenReceived={(token) => {
                            form.handleSubmit((values) =>
                              onSubmit({
                                ...values,
                                recaptchaToken: token,
                              })
                            )();
                          }}
                        />
                      </div>
                      <p className="text-center text-[9px] text-foreground/45 mt-1.5 uppercase tracking-wider flex items-center justify-center gap-1 font-bold whitespace-nowrap">
                        <ShieldCheck className="w-3 h-3 flex-shrink-0" /> Encrypted · Never shared with third parties
                      </p>
                    </form>
                </Form>
              </>
            )}
          </div>
        </div>
      );
    }

    const RecaptchaSubmitButton = ({ onTokenReceived, isSubmitting }: { onTokenReceived: (token: string) => void, isSubmitting: boolean }) => { const { executeRecaptcha } = useGoogleReCaptcha(); const handleClick = async (e: React.MouseEvent) => { e.preventDefault(); try { if (!executeRecaptcha) { console.log("ReCAPTCHA henüz hazır değil"); return; } const token = await executeRecaptcha("appointment_form"); if (!token) { console.log("Token alınamadı"); return; } onTokenReceived(token); } catch (err) { console.error("Recaptcha error:", err); } }; return ( <Button type="button" onClick={handleClick} disabled={isSubmitting} className="w-full bg-primary ..." > {isSubmitting ? "Sending..." : "Book Appointment"} </Button> ); };

    function SuccessScreen({ onReset }: { onReset: () => void }) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="py-6 flex flex-col items-center justify-center text-center"
        >
          {/* Animated check */}
          <div className="relative mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
              className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/30 to-primary/5 border border-primary/30 flex items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-[0_0_30px_rgba(200,165,110,0.5)]"
              >
                <Check className="w-8 h-8 text-primary-foreground" strokeWidth={3} />
              </motion.div>
            </motion.div>
            {/* Sparkles */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="absolute -top-2 -right-2"
            >
              <Sparkles className="w-5 h-5 text-primary animate-pulse" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold mb-2">
              Request Received
            </div>
            <h4 className="text-2xl md:text-3xl font-extrabold mb-3 tracking-tight text-white">
              Welcome to <span className="text-white">rememore</span>
            </h4>
            <p className="text-sm text-foreground/75 font-medium leading-relaxed mb-2 max-w-sm">
              Your request has been securely transmitted. A senior patient
              coordinator will reach out personally within{" "}
              <strong className="text-foreground">24 hours</strong>.
            </p>
            <p className="text-xs text-foreground/55 font-medium mb-7">
              Please check your inbox (and spam folder) for our discreet
              confirmation email.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="w-full space-y-3"
          >
            <Button
              className="w-full bg-green-500 hover:bg-green-600 text-white h-12 rounded-xl font-bold tracking-wide uppercase text-xs shadow-[0_0_20px_rgba(34,197,94,0.35)]"
              onClick={() => window.open(WHATSAPP_LINK, "_blank")}
            >
              <FaWhatsapp className="mr-2 w-5 h-5" />
              Continue on WhatsApp Now
            </Button>
            <button
              onClick={onReset}
              className="text-xs text-foreground/50 hover:text-primary transition-colors font-bold uppercase tracking-widest"
            >
              Submit another request
            </button>
          </motion.div>
        </motion.div>
      );
    }

    function ProcedureCard({
      img,
      num,
      title,
      desc,
      features,
      openWhatsApp,
      openConsultation,
    }: {
      img: string;
      num: string;
      title: string;
      desc: string;
      features: string[];
      openWhatsApp: () => void;
      openConsultation: () => void;
    }) {
      return (
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden flex flex-col md:flex-row hover:border-primary/40 transition-all duration-500 group hover:-translate-y-1"
        >
          {/* Image */}
          <div className="relative md:w-2/5 h-32 md:h-auto flex-shrink-0 overflow-hidden">
            <img
              src={img}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-card/60"></div>
          </div>

          {/* Content */}
          <div className="p-3 md:p-7 flex flex-col flex-grow">
            <h3 className="text-sm md:text-2xl font-extrabold mb-2 md:mb-3 leading-tight tracking-tight">
              {title}
            </h3>
            <p className="hidden md:block text-sm text-foreground/75 font-medium leading-relaxed mb-5">
              {desc}
            </p>

            <ul className="flex flex-col gap-1 md:gap-2 mb-2">
              {features.map((feat, i) => (
                <li
                  key={i}
                  className="flex items-center gap-1.5 text-[10px] md:text-xs font-bold text-foreground/85 capitalize whitespace-nowrap"
                >
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                  {feat}
                </li>
              ))}
            </ul>

            {/* Single Book CTA — always visible on mobile; fades in on card hover for desktop only */}
            <div className="mt-3 md:mt-5 md:opacity-0 md:group-hover:opacity-100 md:transition-opacity md:duration-500">
              <Button
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-9 md:h-11 rounded-lg font-extrabold tracking-wide uppercase text-[11px] md:text-xs"
                onClick={openConsultation}
              >
                Book
              </Button>
            </div>
          </div>
        </motion.article>
      );
    }

    function CertificateMedal() {
      const [tilt, setTilt] = useState({ x: 0, y: 0 });

      const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 25;
        const y = -((e.clientY - rect.top) / rect.height - 0.5) * 25;
        setTilt({ x, y });
      };
      const onLeave = () => setTilt({ x: 0, y: 0 });

      return (
        <div
          className="relative w-full max-w-[100px] md:max-w-[200px] mx-auto aspect-square"
          style={{ perspective: "1200px" }}
          onMouseMove={onMouseMove}
          onMouseLeave={onLeave}
        >
          {/* Glow */}
          <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full"></div>

          <div
            className="relative w-full h-full transition-transform duration-300 ease-out group"
            style={{
              transform: `rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
              transformStyle: "preserve-3d",
            }}
          >
            {/* Outer ring */}
            <div
              className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/30 via-primary/10 to-transparent border border-primary/30"
              style={{ transform: "translateZ(20px)" }}
            ></div>

            {/* Medal */}
            <div
              className="absolute inset-6 rounded-full bg-gradient-to-br from-card to-secondary border border-white/15 flex items-center justify-center overflow-hidden"
              style={{ transform: "translateZ(40px)" }}
            >
              <img
                src={jciCertificate}
                alt="The Joint Commission – National Quality Approval"
                className="w-[98%] h-[98%] object-contain drop-shadow-[0_10px_25px_rgba(200,165,110,0.4)]"
              />

              {/* Shine sweep */}
              <div className="absolute inset-0 overflow-hidden rounded-full pointer-events-none">
                <div className="absolute -inset-y-4 -left-1/2 w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-0 group-hover:translate-x-[400%] transition-transform duration-1500 ease-out"></div>
              </div>
            </div>

          </div>
        </div>
      );
    }

    function FAQItem({ value, q, a }: { value: string; q: string; a: string }) {
      return (
        <AccordionItem
          value={value}
          className="border-b border-white/10 last:border-0"
        >
          <AccordionTrigger className="text-left font-bold text-sm md:text-lg hover:text-primary transition-colors hover:no-underline [&[data-state=open]]:text-primary py-3 md:py-5">
            {q}
          </AccordionTrigger>
          <AccordionContent className="text-foreground/75 font-medium leading-relaxed text-xs md:text-base pb-4 md:pb-5 pt-1">
            {a}
          </AccordionContent>
        </AccordionItem>
      );
    }

    /* ----------------- Personalized Plan Quiz Modal ----------------- */

    type QuizAnswers = {
      age?: string;
      concern?: string;
      size?: string;
    };

    type Package = {
      id: string;
      name: string;
      price: string;
      tagline: string;
      includes: string[];
      badge?: string;
    };

    const PACKAGES: Record<string, Package> = {
      girth: {
        id: "girth",
        name: "Penis Girth Enhancement",
        price: "$4,000",
        tagline: "Permanent girth via your own purified fat cells.",
        includes: ["Surgery", "VIP transfers", "5★ hotel stay", "Lifetime aftercare"],
      },
      enlargement: {
        id: "enlargement",
        name: "Penis Enlargement",
        price: "$4,000",
        tagline: "Soft-tissue dermal graft for natural, permanent length.",
        includes: ["Surgery", "VIP transfers", "5★ hotel stay", "Lifetime aftercare"],
      },
      alpha: {
        id: "alpha",
        name: "Alpha Man Package",
        price: "$7,500",
        tagline: "Length + girth combined — our complete signature transformation.",
        includes: [
          "Combined surgery",
          "VIP transfers",
          "7-night 5★ hotel",
          "Concierge medical coordinator",
          "Lifetime aftercare",
        ],
        badge: "Most Popular",
      },
      implant1: {
        id: "implant1",
        name: "Penile Implant — Single Piece",
        price: "$4,000",
        tagline: "Reliable on-demand erection with a malleable prosthesis.",
        includes: ["Surgery", "VIP transfers", "5★ hotel stay", "Lifetime aftercare"],
      },
      implant3: {
        id: "implant3",
        name: "Penile Implant — Three Piece",
        price: "$9,000",
        tagline: "The premium inflatable solution with the most natural feel.",
        includes: [
          "Surgery",
          "VIP transfers",
          "7-night 5★ hotel",
          "Concierge medical coordinator",
          "Lifetime aftercare",
        ],
        badge: "Premium",
      },
      curvature: {
        id: "curvature",
        name: "Penile Curvature Correction",
        price: "From $4,500",
        tagline: "Surgical straightening that restores form and function.",
        includes: ["Surgery", "VIP transfers", "5★ hotel stay", "Lifetime aftercare"],
      },
      custom: {
        id: "custom",
        name: "Custom Treatment Plan",
        price: "On Consultation",
        tagline: "A bespoke surgical plan built personally by Dr. Alpha.",
        includes: [
          "Private consultation",
          "Personalized plan",
          "VIP transfers",
          "Lifetime aftercare",
        ],
      },
    };

    function recommendPackage(a: QuizAnswers): Package {
      const small = a.size === "<10" || a.size === "10-13";
      switch (a.concern) {
        case "thickness":
          return small ? PACKAGES.alpha : PACKAGES.girth;
        case "length":
          return small ? PACKAGES.alpha : PACKAGES.enlargement;
        case "curvature":
          return PACKAGES.curvature;
        case "ed":
          return a.age === "46+" ? PACKAGES.implant3 : PACKAGES.implant1;
        case "other":
        default:
          return PACKAGES.custom;
      }
    }


    /* ----------------- Consultation Dialog ----------------- */

    function ConsultationDialog({
      open,
      onOpenChange,
      form,
      onSubmit,
      formSuccess,
      resetForm,
      onPrivacyClick,
    }: {
      open: boolean;
      onOpenChange: (o: boolean) => void;
      form: ReturnType<typeof useForm<z.infer<typeof formSchema>>>;
      onSubmit: (v: z.infer<typeof formSchema>) => void;
      formSuccess: boolean;
      resetForm: () => void;
      onPrivacyClick?: () => void;
    }) {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="sm:max-w-lg bg-transparent border-0 p-0 shadow-none max-h-[92vh] overflow-visible">
            <DialogTitle className="sr-only">Request a Consultation</DialogTitle>
            <DialogDescription className="sr-only">
              Private and confidential consultation request form.
            </DialogDescription>
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[3rem] blur-3xl pointer-events-none"></div>
              <div className="shine-border relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[2rem] p-6 md:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.7)] max-h-[88vh] overflow-y-auto">
                <ConsultationCard
                  form={form}
                  onSubmit={onSubmit}
                  formSuccess={formSuccess}
                  resetForm={resetForm}
                  onPrivacyClick={onPrivacyClick}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    function PrivacyDialog({
      open,
      onOpenChange,
      onAccept,
    }: {
      open: boolean;
      onOpenChange: (o: boolean) => void;
      onAccept?: () => void;
    }) {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="sm:max-w-lg bg-transparent border-0 p-0 shadow-none max-h-[92vh] overflow-visible">
            <DialogTitle className="sr-only">Privacy Notice</DialogTitle>
            <DialogDescription className="sr-only">
              How rememore handles personal information from consultation requests.
            </DialogDescription>
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[3rem] blur-3xl pointer-events-none"></div>
              <div className="relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[2rem] p-5 md:p-7 shadow-[0_30px_80px_rgba(0,0,0,0.7)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
                <div className="pr-8">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-primary font-bold">
                      Confidentiality First
                    </span>
                  </div>
                  <h3 className="font-extrabold text-xl md:text-2xl mb-2 tracking-tight leading-tight">
                    Privacy <span className="text-primary">Notice</span>
                  </h3>
                  <p className="text-[11px] text-foreground/55 mb-4 font-medium uppercase tracking-wider">
                    Last updated · April 2026
                  </p>
                </div>

                <div className="space-y-4 text-[13px] md:text-sm text-foreground/80 font-medium leading-relaxed">
                  <section>
                    <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                      1. What We Collect
                    </h4>
                    <p>
                      When you request a consultation we collect the information you choose to share with us:
                      full name, email address, phone number with country code, and any details you add about your goals.
                      We do not collect medical records at this stage.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                      2. How We Use It
                    </h4>
                    <p>
                      Your information is used solely to respond personally to your request, schedule a private
                      consultation, and prepare a confidential treatment plan with Prof. Dr. Abdulmuttalip Şimşek
                      and the rememore team. We never use your data for unrelated marketing.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                      3. Who Has Access
                    </h4>
                    <p>
                      Access is restricted to your dedicated senior advisor, the surgical team, and the Memorial
                      Hospitals coordination office. Your data is never sold, rented, or shared with third-party
                      marketers, advertisers, or social platforms.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                      4. Storage &amp; Security
                    </h4>
                    <p>
                      Submissions are transmitted over encrypted (TLS) connections and stored in access-controlled
                      systems within the EU. Patient identifiers used in published testimonials are anonymized.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                      5. Your Rights
                    </h4>
                    <p>
                      You may request access to, correction of, or deletion of your personal data at any time by
                      replying to any message from our team or by contacting us via WhatsApp. We will action verified
                      requests within 30 days.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                      6. Cookies &amp; Analytics
                    </h4>
                    <p>
                      This website uses only essential cookies required for the form and video previews to function.
                      No advertising or cross-site tracking cookies are set.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                      7. Contact
                    </h4>
                    <p>
                      Questions about this notice? Reach the rememore privacy desk through the WhatsApp button at
                      the bottom of this page and a senior advisor will respond within 24 hours.
                    </p>
                  </section>
                </div>

                <div className="mt-5 pt-4 border-t border-white/10 px-1 pb-1">
                  <Button
                    type="button"
                    onClick={() => {
                      onAccept?.();
                      onOpenChange(false);
                    }}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-xl font-extrabold tracking-[0.3em] uppercase text-xs md:text-sm shadow-[0_0_22px_rgba(200,165,110,0.32)] transition-all"
                  >
                    Got it · Accept
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    /* ----------------- Personalized Plan Section ----------------- */

    function PersonalizedPlanSection({
      drAlpha,
      onBook,
      onWhatsApp,
    }: {
      drAlpha: string;
      onBook: () => void;
      onWhatsApp: () => void;
    }) {
      const [age, setAge] = useState<string | null>(null);
      const [concern, setConcern] = useState<string | null>(null);
      const [size, setSize] = useState<number | null>(null);

      const sizeBucket: string | null =
        size === null
          ? null
          : size < 10
            ? "<10"
            : size <= 13
              ? "10-13"
              : size <= 17
                ? "14-17"
                : "18+";

      const allAnswered = age !== null && concern !== null && sizeBucket !== null;
      // Default to Standard (Alpha) package — updates live as user answers
      const recommended = allAnswered
        ? recommendPackage({ age: age!, concern: concern!, size: sizeBucket! })
        : PACKAGES.alpha;

      const concernOptions = [
        { v: "length", label: "Length", Icon: Maximize2 },
        { v: "thickness", label: "Girth", Icon: Ruler },
        { v: "curvature", label: "Curvature", Icon: Compass },
        { v: "ed", label: "Erectile", Icon: Zap },
        { v: "other", label: "Other", Icon: HelpCircle },
      ];

      const ageOptions = [
        { v: "<30", label: "Under 30" },
        { v: "31-45", label: "31 — 45" },
        { v: "46+", label: "46+" },
      ];

      const stepsCompleted =
        (age !== null ? 1 : 0) + (concern !== null ? 1 : 0) + (size !== null ? 1 : 0);
      const progressPct = (stepsCompleted / 3) * 100;
      const ageDone = age !== null;
      const concernDone = concern !== null;
      const sizeDone = size !== null;

      return (
        <div className="relative pt-4">
          {/* Decorative layer — isolated overflow so it never clips card glows */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Decorative grid lines */}
            <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(200,165,110,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(200,165,110,0.5) 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
          </div>

          {/* Floating badge accents */}
          <div className="absolute top-12 left-8 hidden lg:flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-1.5 backdrop-blur-md z-10">
            <Lock className="w-3 h-3 text-primary" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-foreground/70">100% Private</span>
          </div>
          <div className="absolute top-12 right-8 hidden lg:flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-1.5 backdrop-blur-md z-10">
            <Sparkles className="w-3 h-3 text-primary" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-foreground/70">AI-Matched</span>
          </div>

          <div className="relative">
            <SectionHeader
              eyebrow="03 — Personalized Plan"
              title={
                <>
                  Find Your <span className="text-primary">Package</span>
                  <br />
                  in 30 Seconds
                </>
              }
              subtitle="Three quick questions — private, no email required."
            />
            <div className="mb-8" />

            <div className="grid lg:grid-cols-5 gap-4 md:gap-6 max-w-7xl mx-auto items-stretch">
              {/* Left: Quiz form (compact, gamified) */}
              <div className="lg:col-span-3 relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-xl border border-white/12 rounded-3xl p-4 md:p-6 overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"></div>

                {/* Gamified header — steps progress + XP-style stat */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-3.5 h-3.5 text-primary" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-foreground/70">
                      Quick Quiz
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-primary">
                    {stepsCompleted} / 3 Done
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mb-5">
                  {[ageDone, concernDone, sizeDone].map((done, idx) => (
                    <div
                      key={idx}
                      className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden"
                    >
                      <motion.div
                        initial={false}
                        animate={{ width: done ? "100%" : "0%" }}
                        transition={{ duration: 0.45, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-primary/70 to-primary shadow-[0_0_8px_rgba(200,165,110,0.5)]"
                      />
                    </div>
                  ))}
                </div>

                {/* Step 1 - Age */}
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold flex items-center justify-center">
                      1
                    </div>
                    <h3 className="text-xs font-extrabold uppercase tracking-wide">
                      Your Age Range
                    </h3>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {ageOptions.map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setAge(o.v)}
                        className={`h-10 rounded-lg border text-xs font-extrabold uppercase tracking-wide transition-all duration-300 ${age === o.v
                            ? "bg-primary text-primary-foreground border-primary shadow-[0_0_18px_rgba(200,165,110,0.4)]"
                            : "bg-white/[0.04] border-white/10 hover:border-primary/40 hover:bg-white/[0.08]"
                          }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 2 - Concern */}
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold flex items-center justify-center">
                      2
                    </div>
                    <h3 className="text-xs font-extrabold uppercase tracking-wide">
                      Primary Goal
                    </h3>
                  </div>
                  <div className="grid grid-cols-6 sm:grid-cols-5 gap-1.5">
                    {concernOptions.map((o, i) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setConcern(o.v)}
                        className={`flex flex-col items-center justify-center gap-1 h-14 sm:h-16 rounded-lg border text-[10px] font-extrabold uppercase tracking-wide transition-all duration-300 ${i < 3 ? "col-span-2 sm:col-span-1" : "col-span-3 sm:col-span-1"
                          } ${concern === o.v
                            ? "bg-primary text-primary-foreground border-primary shadow-[0_0_18px_rgba(200,165,110,0.4)]"
                            : "bg-white/[0.04] border-white/10 hover:border-primary/40 hover:bg-white/[0.08]"
                          }`}
                      >
                        <o.Icon className="w-4 h-4" />
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 3 - Size slider (gamified) */}
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold flex items-center justify-center">
                      3
                    </div>
                    <h3 className="text-xs font-extrabold uppercase tracking-wide">
                      Current Length (Erect)
                    </h3>
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-[0.25em] text-foreground/55 font-bold">
                        {size === null ? "Drag to set" : "Approximate"}
                      </span>
                      <span
                        className={`text-2xl font-extrabold tracking-tight ${size === null ? "text-foreground/30" : "text-primary"
                          }`}
                      >
                        {size === null ? "—" : size}
                        <span className="text-xs text-foreground/60 ml-1 font-bold">cm</span>
                      </span>
                    </div>
                    <Slider
                      value={[size ?? 15]}
                      onValueChange={(v) => setSize(v[0])}
                      min={8}
                      max={22}
                      step={1}
                      aria-label="Current length in centimeters"
                      className={`my-1 ${size === null ? "opacity-50" : ""}`}
                    />
                    <div className="flex justify-between text-[9px] uppercase tracking-widest text-foreground/45 font-bold mt-2">
                      <span>8 cm</span>
                      <span>22 cm</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-foreground/55 mt-2 leading-relaxed font-medium flex items-start gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-primary flex-shrink-0 mt-0.5" />
                    Private — used only to suggest your package.
                  </p>
                </div>
              </div>

              {/* Right: Live recommendation — defaults to Standard (Alpha) package */}
              <motion.div
                key={recommended.id}
                initial={{ opacity: 0.4, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="lg:col-span-2 relative bg-gradient-to-br from-primary/15 via-white/[0.05] to-white/[0.02] backdrop-blur-xl border border-primary/35 rounded-3xl p-4 md:p-6 flex flex-col recommended-glow shine-border"
              >
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-primary font-extrabold flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Your Match
                  </span>
                  {recommended.badge && (
                    <span className="text-[9px] font-extrabold tracking-[0.2em] uppercase bg-primary text-primary-foreground rounded-full px-2.5 py-1 shadow-lg">
                      {recommended.badge}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="relative w-12 h-12 rounded-full overflow-hidden border-2 border-primary/40 flex-shrink-0 bg-[#0d0d0d]">
                    <img
                      src={drAlpha}
                      alt="Dr. Alpha"
                      className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.25em] text-foreground/60 font-extrabold mb-0.5">
                      Recommended by
                    </div>
                    <div className="text-xs font-extrabold leading-tight">
                      Prof. Dr. Şimşek · Dr. Alpha
                    </div>
                  </div>
                </div>

                <h3 className="text-base md:text-lg font-extrabold tracking-tight mb-1.5 leading-tight">
                  {recommended.name}
                </h3>
                <p className="text-xs text-foreground/75 font-medium leading-relaxed mb-3 line-clamp-2 min-h-[2.8em]">
                  {recommended.tagline}
                </p>

                <div className="bg-black/30 border border-white/10 rounded-xl px-4 py-3 mb-3">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-foreground/55 font-extrabold mb-0.5">
                    All-Inclusive From
                  </div>
                  <div className="text-2xl md:text-3xl font-extrabold text-primary tracking-tight leading-none whitespace-nowrap">
                    {recommended.price}
                  </div>
                </div>

                <ul className="space-y-1.5 mb-4 flex-grow">
                  {recommended.includes.slice(0, 4).map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[12px] font-medium text-foreground/85"
                    >
                      <Check className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="grid grid-cols-1 gap-2">
                  <Button
                    onClick={onBook}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-xl font-extrabold tracking-wide uppercase text-[11px] shadow-[0_0_25px_rgba(200,165,110,0.35)]"
                  >
                    Book This Plan
                  </Button>
                  <Button
                    onClick={onWhatsApp}
                    variant="outline"
                    className="border-white/15 hover:bg-white/5 h-11 rounded-xl font-extrabold tracking-wide uppercase text-[11px] bg-white/[0.03]"
                  >
                    <FaWhatsapp className="mr-2 w-4 h-4 text-green-400" />
                    Discuss on WhatsApp
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      );
    }
